// Modal "Nova viagem" — wizard multi-etapas.
//
// Etapas (totalSteps é dinâmico, calculado a partir de routeMode/aiFlow):
//   1. Básico         — título, período, cidade de partida, destinos
//   2. Roteiro        — escolha do modo (IA / manual / existente)
//   3. IA (config)    — *só se routeMode === 'ai'*
//   4. Importar       — *só se aiFlow === 'prompt'*
//   4. Revisar        — *só se aiFlow === 'generate'* — gera via Gemini e
//                       mostra um resumo do roteiro antes de criar a viagem
//   N. Convidados     — *sempre a última etapa*, opcional
//
// Caminhos suportados:
//   - Manual:  Básico → Roteiro → Convidados   (3 etapas, CTA "Criar viagem")
//   - Preset:  estrutura pronta, sub-painel = stub "em breve" (Fase 2)
//   - IA:      Básico → Roteiro → IA → IA(extra) → Importar/Revisar → Convidados
//              · 'prompt'   — copia o prompt, o user importa o JSON depois
//              · 'generate' — gera direto no app via Gemini (allowlist aiAccess)
//
// Save:
//   - Cria trip via window.tripsApi.create(...)
//   - Pra cada email: gera token, cria invites/{token}, guarda em
//     trip.pendingInvites
//   - Mostra modal final com links pra copiar (sem disparo de email
//     automático nessa etapa).

import { auth } from '../../core/firebase.js';
import { db } from '../../core/firebase.js';
import { doc, setDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { mountTripDateRangeField } from '../../shared/components/tripDateRange.js';
import { createUserInviteAutocomplete } from '../../shared/components/userInviteAutocomplete.js';
import { loadGoogleMaps } from '../map/places.js';
import { attachPlacesAutocomplete } from '../../shared/components/placesAutocomplete.js';
import { parseAiResponse, validateAiPayload, convertToInternal } from './aiImport.js';
import { importAiPayload } from './aiImportClient.js';
import { authFetch } from '../../core/auth.js';
import { API } from '../../core/api.js';
import { getAiQuota } from './aiAccessGate.js';
import { updateTrip } from '../../core/firestore.js';
import { saveTripSettings } from '../../core/tripSettings.js';
import { fetchApiRates } from '../../core/currencyApi.js';
import { inviteUrl } from '../../core/site.js';
import {
  loadNewTripDraft, applyNewTripDraft, persistNewTripDraft, clearNewTripDraft,
} from './newTripDraft.js';
import { toast } from '../../shared/components/toast.js';

const ICONS = {
  close:    '<path d="M18 6L6 18M6 6l12 12"/>',
  user:     '<circle cx="12" cy="8" r="4"/><path d="M4 21c0-4 4-7 8-7s8 3 8 7"/>',
  search:   '<circle cx="11" cy="11" r="7"/><path d="M20 20l-3.5-3.5"/>',
  trash:    '<path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>',
  copy:     '<rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>',
  check:    '<path d="M20 6L9 17l-5-5"/>',
  edit:     '<path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/>',
  map:      '<path d="M9 3L3 6v15l6-3 6 3 6-3V3l-6 3-6-3z"/><path d="M9 3v15"/><path d="M15 6v15"/>',
  sparkles: '<path d="M12 3l1.6 4.8L18 9l-4.4 1.2L12 15l-1.6-4.8L6 9l4.4-1.2L12 3z"/><path d="M18 14l.9 2.7L21 18l-2.1.9L18 22l-.9-2.1L15 18l2.1-.9L18 14z"/>',
  arrowL:   '<path d="M19 12H5"/><path d="M12 19l-7-7 7-7"/>',
  arrowR:   '<path d="M5 12h14"/><path d="M12 5l7 7-7 7"/>',
  mail:     '<rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 7l9 6 9-6"/>',
  // Ícones novos pros labels da Nova Viagem (paths estilo Lucide).
  type:     '<polyline points="4 7 4 4 20 4 20 7"/><line x1="9" y1="20" x2="15" y2="20"/><line x1="12" y1="4" x2="12" y2="20"/>',
  calendar: '<rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>',
  users:    '<path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/>',
  plane:    '<path d="M21 16v-2l-8-5V3.5a1.5 1.5 0 0 0-3 0V9l-8 5v2l8-2.5V19l-2 1.5V22l3.5-1 3.5 1v-1.5L13 19v-5.5z"/>',
  pin:      '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>',
  wallet:   '<path d="M20 12V8H6a2 2 0 0 1-2-2c0-1.1.9-2 2-2h12v4"/><path d="M4 6v12a2 2 0 0 0 2 2h14v-4"/><path d="M18 12a2 2 0 0 0-2 2c0 1.1.9 2 2 2h4v-4z"/>',
  tag:      '<path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"/><line x1="7" y1="7" x2="7.01" y2="7"/>',
  compass:  '<circle cx="12" cy="12" r="10"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76"/>',
  list:     '<line x1="8" y1="6" x2="21" y2="6"/><line x1="8" y1="12" x2="21" y2="12"/><line x1="8" y1="18" x2="21" y2="18"/><line x1="3" y1="6" x2="3.01" y2="6"/><line x1="3" y1="12" x2="3.01" y2="12"/><line x1="3" y1="18" x2="3.01" y2="18"/>',
  bookmark: '<path d="M19 21l-7-5-7 5V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2z"/>',
  message:  '<path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/>',
  route:    '<circle cx="6" cy="19" r="3"/><path d="M9 19h8.5a3.5 3.5 0 0 0 0-7h-11a3.5 3.5 0 0 1 0-7H15"/><circle cx="18" cy="5" r="3"/>',
};

// Logo oficial do WhatsApp (verde) — botões de compartilhamento dos links de
// convite. SVG inline pra evitar requisição extra.
const WHATSAPP_SVG = `
  <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#25D366" d="M20.52 3.449C18.24 1.245 15.24 0.03 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.547 4.142 1.588 5.945L0 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.581 0 11.94-5.335 11.943-11.893A11.821 11.821 0 0 0 20.52 3.45zM12.05 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.999-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.437-9.884 9.889-9.884a9.825 9.825 0 0 1 6.991 2.898 9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.438 9.884-9.89 9.884zm5.422-7.403c-.297-.149-1.758-.867-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.149-.173.198-.297.298-.495.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01a1.094 1.094 0 0 0-.793.371c-.272.297-1.04 1.016-1.04 2.479s1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.247-.694.247-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/>
  </svg>
`;

// Caminhos de roteiro habilitados.
// - 'manual'  avança end-to-end.
// - 'preset'  selecionável; sub-painel mostra lista (Firestore stub).
// - 'ai'      leva à Etapa AI (config) com dois sub-fluxos:
//     · 'generate' — gera o roteiro via Gemini direto no app. Liberado por
//       allowlist (aiAccess/{uid}); card desabilitado pra quem não tem acesso.
//     · 'prompt'   — copia o prompt; o user gera o JSON na IA dele e importa.
const ROUTE_MODES_FUNCTIONAL = new Set(['manual', 'preset', 'ai']);

// Limite de duração da viagem. O período é capado no seletor de datas e no
// slider de dias; acima de TRIP_DAYS_WARN o modal mostra um aviso. O teto
// existe porque viagens longas geram roteiros muito grandes pra IA.
const MAX_TRIP_DAYS = 33;
const TRIP_DAYS_WARN = 30;

/** Nº de dias que a viagem terá conforme o STATE atual (0 se indefinido). */
function effectiveTripDays(state) {
  if (state.datesUndecided) {
    return Number.isFinite(state.numDays) ? state.numDays : 0;
  }
  if (state.start && state.end) {
    const ms = new Date(state.end).getTime() - new Date(state.start).getTime();
    if (!Number.isFinite(ms) || ms < 0) return 0;
    return Math.round(ms / 86_400_000) + 1; // inclusivo (ida + volta)
  }
  return 0;
}

/** Abre o modal. Retorna a Promise da trip criada (ou null se cancelado). */
export function openNewTripModal() {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'settings-modal newtrip-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');

    const STATE = {
      step: 1,

      // Etapa 1: Básico
      title: '',
      datesUndecided: false,
      start: '',
      end: '',
      numDays: 7,
      // Moeda principal fixa em BRL por ora. Cotações de outras moedas
      // passam a ser configuradas no passo "Gastos" (etapa futura).
      currency: 'BRL',
      places: [],
      adults: 2,
      children: 0,

      // Etapa 2: Roteiro
      routeMode: null,   // 'manual' | 'preset' | 'ai'
      presetId: null,
      presets: [],       // cache da lista carregada via loadPresets()
      presetsLoading: false,
      presetsError: '',

      // Etapa 3 (Config IA) — passos Gastos + Detalhes
      expensesUndefined: false,     // switch "Não definir gastos agora"
      // Cotações da viagem agora são definidas pela IA (com base no destino)
      // e persistidas em trips/{id}/settings via aiImport. O usuário não
      // configura mais cotações no wizard.
      tripRates:     null,
      // Teto de gastos: dois modos. 'category' usa um dos 4 tiers; 'amount'
      // recebe um valor de orçamento total da viagem (em BRL).
      budgetMode:    'category',    // 'category' | 'amount'
      budgetAmount:  null,          // BRL — só usado quando budgetMode === 'amount'
      budgetTier:    'mid',         // 'low' | 'mid' | 'high' | 'lux'
      expenseDetail: 'category',    // 'category' | 'item'
      travelStyle:   'equilibrada', // 'tranquila' | 'equilibrada' | 'energica'
      itineraryMode: 'detailed',    // 'detailed' | 'basic' | 'free'
      // "Conte pra gente o que já tem" — texto livre com itens já contratados
      // (passagem, hotel, ingresso) que a IA deve respeitar no roteiro.
      alreadyHave:   '',
      departureCity: '',
      aiNotes:       '',
      aiFlow:        null,          // 'generate' | 'prompt'
      promptCopied:  false,
      // Cota de geração com IA (aiAccess/{uid}). null = ainda verificando;
      // objeto { used, remaining, unlimited, allowed } = resultado do check.
      aiQuota:       null,

      // Etapa Importar (só no fluxo aiFlow === 'prompt' — upload do JSON).
      // O fluxo 'generate' não usa estes campos: a geração roda no backend.
      importedFileName: '',
      importedFileSize: 0,
      importedRaw:      '',
      importedData:     null,       // payload validado da IA (objeto)
      importedInternal: null,       // { tripPayload, timeline, itinerary, hotels, expenses, summary }
      importError:      '',

      // Convidados — etapa removida do wizard; guestEmails fica vazio.
      guestEmails: [],

      // Submit
      submitting: false,
      error: '',
    };

    // Restaura rascunho salvo (se houver) por cima do STATE inicial ANTES de
    // renderizar — assim campos templados pelo state (nº de dias, viajantes)
    // já saem preenchidos. Os inputs de texto são hidratados após o bind.
    const _draft = loadNewTripDraft();
    if (_draft) applyNewTripDraft(STATE, _draft);

    overlay.innerHTML = MODAL_HTML(STATE);
    document.body.appendChild(overlay);

    // ─── Element handles ────────────────────────────────────────
    const els = {
      // Shell
      eyebrow:      overlay.querySelector('[data-slot="eyebrow"]'),
      titleH:       overlay.querySelector('[data-slot="step-title"]'),
      stepper:      overlay.querySelector('[data-slot="stepper"]'),
      counter:      overlay.querySelector('[data-slot="counter"]'),
      footLeft:     overlay.querySelector('[data-slot="foot-left"]'),
      footRight:    overlay.querySelector('[data-slot="foot-right"]'),
      errorBox:     overlay.querySelector('[data-slot="error"]'),
      form:         overlay.querySelector('form'),

      // Steps containers (todos sempre no DOM, só ocultados)
      stepBasic:    overlay.querySelector('[data-step="basic"]'),
      stepRoute:    overlay.querySelector('[data-step="route"]'),
      stepInvites:  overlay.querySelector('[data-step="invites"]'),

      // Etapa 1
      title:        overlay.querySelector('#nt-title'),
      dateRange:    overlay.querySelector('[data-slot="date-range"]'),
      numdaysField: overlay.querySelector('[data-slot="numdays-field"]'),
      numdaysInput: overlay.querySelector('#nt-numdays'),
      numdaysOut:   overlay.querySelector('[data-slot="numdays-value"]'),
      daysWarn:     overlay.querySelector('[data-slot="days-warn"]'),
      placeInput:   overlay.querySelector('#nt-place-input'),
      placeChips:   overlay.querySelector('[data-slot="place-chips"]'),

      // Etapa 2
      routeCards:   overlay.querySelectorAll('[data-route-card]'),
      presetPanel:  overlay.querySelector('[data-slot="preset-panel"]'),

      // Etapa 3 (Config IA) — dividida em 2 painéis (ai + aiExtra)
      stepAi:        overlay.querySelector('[data-step="ai"]'),
      stepAiExtra:   overlay.querySelector('[data-step="aiExtra"]'),
      budgetTiers:   overlay.querySelectorAll('[data-budget-tier]'),
      budgetLegend:  overlay.querySelector('[data-slot="budget-legend"]'),
      budgetModeBtns: overlay.querySelectorAll('[data-budget-mode]'),
      budgetCategoryPane: overlay.querySelector('[data-slot="budget-category-pane"]'),
      budgetAmountPane:   overlay.querySelector('[data-slot="budget-amount-pane"]'),
      budgetAmountInput:  overlay.querySelector('#nt-budget-amount'),
      expenseSegs:   overlay.querySelectorAll('[data-expense-detail]'),
      itineraryRds:  overlay.querySelectorAll('[data-itinerary-mode]'),
      expensesSwitch:  overlay.querySelector('#nt-expenses-undefined'),
      expensesConfig:  overlay.querySelector('[data-slot="expenses-config"]'),
      travelStyles:    overlay.querySelectorAll('[data-travel-style]'),
      alreadyHave:   overlay.querySelector('#nt-already-have'),
      departureCity: overlay.querySelector('#nt-departure-city'),
      aiNotes:       overlay.querySelector('#nt-ai-notes'),
      // Os campos de estilo (ritmo + itinerário) só aparecem no fluxo
      // "Gerar com IA" — no "Copiar prompt" ficam ocultos (valores auto).
      styleFields:   overlay.querySelector('[data-slot="ai-style-fields"]'),
      // Card "Gerar com IA" agora vive na etapa Roteiro; o badge mostra a cota.
      aiGenerateCard:  overlay.querySelector('[data-route-card="ai-generate"]'),
      aiGenerateBadge: overlay.querySelector('[data-slot="ai-generate-badge"]'),
      aiGenerateDesc:  overlay.querySelector('[data-slot="ai-generate-desc"]'),
      promptPanel:   overlay.querySelector('[data-slot="prompt-panel"]'),
      promptPreview: overlay.querySelector('[data-slot="prompt-preview"]'),
      promptCopyBtn: overlay.querySelector('[data-action="copy-prompt"]'),

      // Etapa Importar
      stepImport:     overlay.querySelector('[data-step="import"]'),
      importInput:    overlay.querySelector('#nt-import-input'),
      dropzone:       overlay.querySelector('[data-slot="dropzone"]'),
      dzEmpty:        overlay.querySelector('[data-slot="dz-empty"]'),
      dzFile:         overlay.querySelector('[data-slot="dz-file"]'),
      dzFilename:     overlay.querySelector('[data-slot="dz-filename"]'),
      dzSize:         overlay.querySelector('[data-slot="dz-size"]'),
      importSummary:  overlay.querySelector('[data-slot="import-summary"]'),
      importErrorBox: overlay.querySelector('[data-slot="import-error"]'),
      importModeBtns: overlay.querySelectorAll('[data-import-mode]'),
      importFilePane: overlay.querySelector('[data-slot="import-file-pane"]'),
      importPastePane: overlay.querySelector('[data-slot="import-paste-pane"]'),
      pasteInput:     overlay.querySelector('#nt-import-paste'),
      pasteBtn:       overlay.querySelector('[data-action="paste-process"]'),

      // Etapa final
      guestInviteMount: overlay.querySelector('[data-slot="guest-invite-mount"]'),
      guestChips:   overlay.querySelector('[data-slot="guest-chips"]'),
    };

    // Cleanup do autocomplete de convidados (registra listener global).
    let _guestAcCleanup = null;
    // Handle do seletor de datas (tripDateRange). O calendário flatpickr é
    // anexado ao <body>, NÃO ao overlay do modal — então `overlay.remove()`
    // não o tira da tela. Sem `destroy()` explícito no close, o calendário
    // fica órfão e continua visível depois que o modal fecha.
    let _dateRangeHandle = null;
    // Cleanups dos autocompletes de lugar (destinos + cidade de partida).
    // Removem os `.pac-container` órfãos do <body> no fechamento do modal.
    let _placesAcCleanup = null;
    let _departureAcCleanup = null;
    // Atribuídos no init — permitem que handlers definidos acima (chips de
    // destino, steppers, date range) agendem o save do rascunho.
    let _scheduleDraftSave = null;
    let _saveDraftNow = null;

    // ─── Close / Esc ────────────────────────────────────────────
    function close(result) {
      document.removeEventListener('keydown', onKey);
      if (_guestAcCleanup) { _guestAcCleanup(); _guestAcCleanup = null; }
      if (_dateRangeHandle) { try { _dateRangeHandle.destroy(); } catch {} _dateRangeHandle = null; }
      if (_placesAcCleanup) { try { _placesAcCleanup(); } catch {} _placesAcCleanup = null; }
      if (_departureAcCleanup) { try { _departureAcCleanup(); } catch {} _departureAcCleanup = null; }
      // Viagem criada com sucesso (result truthy) → descarta o rascunho.
      // Cancelar/Esc/fora (result null) mantém pra próxima abertura.
      if (result) clearNewTripDraft();
      overlay.remove();
      resolve(result);
    }
    function onKey(e) {
      if (e.key === 'Escape' && !STATE.submitting) close(null);
    }
    document.addEventListener('keydown', onKey);

    overlay.addEventListener('click', (e) => {
      if (e.target === overlay && !STATE.submitting) close(null);
      if (e.target.closest('[data-action="close"]') && !STATE.submitting) {
        e.preventDefault();
        close(null);
      }
    });

    // ─── Navegação (Voltar/Avançar) ────────────────────────────
    function gotoStep(n) {
      const total = computeTotalSteps(STATE);
      STATE.step = Math.max(1, Math.min(n, total));
      renderShell();
      renderStepVisibility();
    }
    function nextStep() {
      const total = computeTotalSteps(STATE);
      if (STATE.step >= total) {
        submit();
      } else {
        gotoStep(STATE.step + 1);
      }
    }
    function prevStep() {
      if (STATE.step <= 1) {
        if (!STATE.submitting) close(null);
        return;
      }
      gotoStep(STATE.step - 1);
    }

    els.footLeft.addEventListener('click', (e) => {
      e.preventDefault();
      prevStep();
    });
    els.footRight.addEventListener('click', (e) => {
      e.preventDefault();
      nextStep();
    });
    // Stepper clicável — pula direto pra uma etapa. Etapas não-navegáveis são
    // botões `disabled`, então o clique só chega aqui pras liberadas.
    els.stepper?.addEventListener('click', (e) => {
      const btn = e.target.closest('.nt-step');
      if (!btn || btn.disabled) return;
      const n = Number(btn.dataset.stepIndex);
      if (Number.isFinite(n) && n !== STATE.step) gotoStep(n);
    });

    // ─── Etapa 1: Título ────────────────────────────────────────
    els.title?.addEventListener('input', () => {
      STATE.title = els.title.value;
      hideError(els.errorBox);
      renderFooter();
    });

    // ─── Etapa 1: Período (tripDateRange existente) ────────────
    if (els.dateRange) {
      _dateRangeHandle = mountTripDateRangeField(els.dateRange, {
        defaultStart: STATE.start || undefined,
        defaultEnd: STATE.end || undefined,
        defaultUndefined: !!STATE.datesUndecided,
        onChange: (start, end, undef) => {
          STATE.start = start;
          STATE.end = end;
          STATE.datesUndecided = !!undef;
          // Toggle do slider "Número de dias" — visível só quando o user
          // marcou "Ainda não defini a data". Usa classe pra não ser
          // sobrescrito pelo `display: flex` herdado de .newtrip-field.
          if (els.numdaysField) {
            els.numdaysField.classList.toggle('is-shown', !!STATE.datesUndecided);
          }
          renderDaysWarn();
          renderFooter();
          _scheduleDraftSave?.();
        },
      });
    }

    // ─── Etapa 1: Viajantes (adultos/crianças via stepper) ──────────
    overlay.querySelectorAll('[data-traveler]').forEach((wrap) => {
      const field = wrap.dataset.traveler;   // 'adults' | 'children'
      const valEl = wrap.querySelector('[data-traveler-value]');
      const input = wrap.querySelector('[data-traveler-input]');
      const min = Number(input.dataset.min) || 0;
      const max = Number(input.dataset.max) || 99;
      const dec = wrap.querySelector('[data-traveler-dec]');
      const inc = wrap.querySelector('[data-traveler-inc]');
      function update(v) {
        const n = Math.max(min, Math.min(max, v));
        STATE[field] = n;
        valEl.textContent = n;
        input.value = n;
        dec.disabled = n <= min;
        inc.disabled = n >= max;
        renderFooter();
        renderAiPanel?.();
        _scheduleDraftSave?.();
      }
      // Estado inicial dos botões
      dec.disabled = STATE[field] <= min;
      inc.disabled = STATE[field] >= max;
      dec.addEventListener('click', (e) => { e.preventDefault(); update(STATE[field] - 1); });
      inc.addEventListener('click', (e) => { e.preventDefault(); update(STATE[field] + 1); });
    });

    // ─── Etapa 1: Número de dias (slider, só ativo se datesUndecided) ─
    els.numdaysInput?.addEventListener('input', () => {
      const v = parseInt(els.numdaysInput.value, 10);
      if (Number.isFinite(v) && v > 0) {
        STATE.numDays = Math.min(v, MAX_TRIP_DAYS);
        if (els.numdaysOut) els.numdaysOut.textContent = `${STATE.numDays} dia${STATE.numDays === 1 ? '' : 's'}`;
        renderDaysWarn();
        renderFooter();
      }
    });

    // Aviso de duração da viagem — 3 estados:
    //  - até TRIP_DAYS_WARN dias: escondido.
    //  - TRIP_DAYS_WARN+1 a MAX_TRIP_DAYS: aviso âmbar (informativo, segue).
    //  - acima de MAX_TRIP_DAYS: alerta VERMELHO bloqueante (o Avançar
    //    fica desabilitado via isStepValid; o alerta explica o porquê).
    function renderDaysWarn() {
      if (!els.daysWarn) return;
      const n = effectiveTripDays(STATE);
      els.daysWarn.classList.remove('is-shown', 'newtrip-warn--block');
      if (n > MAX_TRIP_DAYS) {
        els.daysWarn.textContent =
          `O limite é de ${MAX_TRIP_DAYS} dias por viagem. Ajuste o período para continuar.`;
        els.daysWarn.classList.add('is-shown', 'newtrip-warn--block');
      } else if (n > TRIP_DAYS_WARN) {
        els.daysWarn.textContent =
          `Viagens longas geram roteiros mais demorados — o limite é de ${MAX_TRIP_DAYS} dias por viagem.`;
        els.daysWarn.classList.add('is-shown');
      } else {
        els.daysWarn.textContent = '';
      }
    }

    // ─── Etapa 1: Destinos ──────────────────────────────────────
    _placesAcCleanup = setupPlacesAutocomplete(els.placeInput, (place) => {
      if (!place) return;
      if (place.placeId && STATE.places.some((p) => p.placeId === place.placeId)) return;
      STATE.places.push(place);
      renderPlaceChips();
      renderFooter();   // places afetam isStepValid('basic')
      _scheduleDraftSave?.();
    });
    function renderPlaceChips() {
      els.placeChips.innerHTML = STATE.places.map((p, i) => `
        <span class="newtrip-chip">
          <span class="newtrip-chip-text">${esc(p.name)}${p.country ? `, <span class="muted">${esc(p.country)}</span>` : ''}</span>
          <button type="button" class="newtrip-chip-remove" data-rm-place="${i}" aria-label="Remover ${esc(p.name)}">${svgIcon('close', 11)}</button>
        </span>
      `).join('');
      els.placeChips.querySelectorAll('[data-rm-place]').forEach((btn) => {
        btn.addEventListener('click', () => {
          STATE.places.splice(Number(btn.dataset.rmPlace), 1);
          renderPlaceChips();
          // Atualiza subtítulo do card de preset se ele estiver visível
          renderRoutePanels();
          renderFooter();   // places afetam isStepValid('basic')
          _scheduleDraftSave?.();
        });
      });
    }

    // ─── Etapa 2: cards de modo ─────────────────────────────────
    // Os cards são: ai-generate | ai-prompt | manual | preset. Os dois de
    // IA setam routeMode='ai' + o aiFlow correspondente.
    els.routeCards.forEach((card) => {
      card.addEventListener('click', (e) => {
        e.preventDefault();
        if (card.classList.contains('is-disabled')) return;
        const rc = card.dataset.routeCard;

        let routeMode;
        let aiFlow;
        if (rc === 'ai-generate')   { routeMode = 'ai'; aiFlow = 'generate'; }
        else if (rc === 'ai-prompt') { routeMode = 'ai'; aiFlow = 'prompt'; }
        else { routeMode = rc; aiFlow = null; }

        // Trocar de fluxo invalida qualquer resultado de import anterior.
        if (aiFlow !== STATE.aiFlow) {
          STATE.importedInternal = null;
          STATE.importedData = null;
          STATE.importError = '';
          STATE.importedFileName = '';
          STATE.importedRaw = '';
        }
        STATE.routeMode = routeMode;
        STATE.aiFlow = aiFlow;

        // "Copiar prompt" pula a etapa de Gastos/Estilo — gastos e estilo
        // são automáticos (roteiro mais simples).
        if (aiFlow === 'prompt') applyCopyPromptDefaults();

        if (routeMode !== 'preset') STATE.presetId = null;
        if (routeMode === 'preset' && !STATE.presetsLoading
            && STATE.presets.length === 0 && !STATE.presetsError) {
          fetchPresets();
        }
        renderRouteCards();
        renderRoutePanels();
        renderShell(); // totalSteps muda conforme routeMode/aiFlow
        _scheduleDraftSave?.();
      });
    });

    // Defaults do fluxo "Copiar prompt" (roteiro mais simples): gastos médios
    // por categoria, ritmo equilibrado, itinerário básico.
    function applyCopyPromptDefaults() {
      STATE.expensesUndefined = false;
      STATE.budgetMode = 'category';
      STATE.budgetTier = 'mid';
      STATE.budgetAmount = null;
      STATE.expenseDetail = 'category';
      STATE.travelStyle = 'equilibrada';
      STATE.itineraryMode = 'basic';
    }

    // ─── Etapa 2: clique em preset item ────────────────────────
    els.presetPanel?.addEventListener('click', (e) => {
      const item = e.target.closest('[data-preset-id]');
      if (!item) return;
      const id = item.dataset.presetId;
      STATE.presetId = STATE.presetId === id ? null : id;
      renderRoutePanels();
      renderFooter();
    });

    // ─── Fetch presets (stub Fase 2; plug-and-play Firestore depois) ───
    async function fetchPresets() {
      STATE.presetsLoading = true;
      STATE.presetsError = '';
      renderRoutePanels();
      try {
        const list = await loadPresets({ places: STATE.places });
        STATE.presets = Array.isArray(list) ? list : [];
      } catch (err) {
        console.warn('[newTrip] loadPresets failed:', err?.code || '', err?.message || err);
        STATE.presetsError = 'Não consegui carregar os roteiros agora.';
      } finally {
        STATE.presetsLoading = false;
        renderRoutePanels();
      }
    }

    // ─── Etapa 3 (Config IA) ────────────────────────────────────
    // Toggle Valor vs Categoria — alterna os 2 painéis dentro de "Teto de gastos".
    els.budgetModeBtns?.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const mode = btn.dataset.budgetMode;       // 'category' | 'amount'
        STATE.budgetMode = mode;
        els.budgetModeBtns.forEach((b) => b.classList.toggle('is-active', b === btn));
        if (els.budgetCategoryPane) els.budgetCategoryPane.hidden = (mode !== 'category');
        if (els.budgetAmountPane)   els.budgetAmountPane.hidden   = (mode !== 'amount');
        renderAiPanel();
      });
    });
    // Categoria escolhida → atualiza legenda da categoria ativa.
    els.budgetTiers?.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        STATE.budgetTier = btn.dataset.budgetTier;
        els.budgetTiers.forEach((b) => b.classList.toggle('is-active', b === btn));
        renderBudgetLegend();
        renderAiPanel();
      });
    });
    function renderBudgetLegend() {
      if (!els.budgetLegend) return;
      els.budgetLegend.textContent = BUDGET_TIER_LEGENDS[STATE.budgetTier] || '';
    }
    renderBudgetLegend();
    // Valor do orçamento (R$) — máscara LIVE estilo calculadora.
    // Cada dígito digitado entra pelas casas decimais e empurra o resto pra
    // esquerda: "1" → 0.01 → "2" → 0.12 → "3" → 1.23 → "4" → 12.34. Apaga
    // pelo backspace cancela o último dígito. Cursor sempre no fim (right-
    // aligned visualmente já que o número cresce pra esquerda).
    if (els.budgetAmountInput) applyBudgetMask(els.budgetAmountInput, (value) => {
      STATE.budgetAmount = value;
      renderAiPanel();
    });
    function renderExpenseDetailLegend() {
      const out = overlay.querySelector('[data-slot="expense-detail-legend"]');
      if (out) out.textContent = EXPENSE_DETAIL_LEGENDS[STATE.expenseDetail] || '';
    }
    els.expenseSegs?.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        STATE.expenseDetail = btn.dataset.expenseDetail;
        els.expenseSegs.forEach((b) => b.classList.toggle('is-active', b === btn));
        renderExpenseDetailLegend();
        renderAiPanel();
      });
    });
    els.itineraryRds?.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        STATE.itineraryMode = btn.dataset.itineraryMode;
        els.itineraryRds.forEach((b) => b.classList.toggle('is-active', b === btn));
        renderAiPanel();
      });
    });
    // Switch "Não definir gastos agora" — esconde o conteúdo de gastos do passo.
    els.expensesSwitch?.addEventListener('change', () => {
      STATE.expensesUndefined = els.expensesSwitch.checked;
      if (els.expensesConfig) els.expensesConfig.hidden = STATE.expensesUndefined;
      renderAiPanel();
    });
    // Estilo da viagem (Tranquila / Equilibrada / Enérgica)
    els.travelStyles?.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        STATE.travelStyle = btn.dataset.travelStyle;
        els.travelStyles.forEach((b) => b.classList.toggle('is-active', b === btn));
        renderAiPanel();
      });
    });
    // Cidade de partida — autocomplete restrito a cidades.
    // Importante: o Google Places seta o input.value (com formatted_address)
    // DEPOIS do place_changed event terminar. Por isso usamos requestAnimationFrame
    // pra ler o valor já preenchido — antes disso o STATE pegava string vazia.
    els.departureCity?.addEventListener('input', () => {
      STATE.departureCity = els.departureCity.value;
      hideError(els.errorBox);
      renderAiPanel();
      renderFooter();   // cidade de partida é obrigatória → afeta isStepValid('basic')
    });
    _departureAcCleanup = setupPlacesAutocomplete(
      els.departureCity,
      (place) => {
        // Próximo frame: o Google já preencheu o input com o nome da cidade
        requestAnimationFrame(() => {
          const val = (els.departureCity?.value || place.name || place.country || '').trim();
          STATE.departureCity = val;
          if (els.departureCity && val && els.departureCity.value !== val) {
            els.departureCity.value = val;
          }
          renderAiPanel();
          renderFooter();   // cidade de partida é obrigatória → afeta isStepValid('basic')
        });
      },
      { types: ['(cities)'], clearAfter: false },
    );
    els.aiNotes?.addEventListener('input', () => {
      STATE.aiNotes = els.aiNotes.value;
      renderAiPanel();
    });
    els.alreadyHave?.addEventListener('input', () => {
      STATE.alreadyHave = els.alreadyHave.value;
      renderAiPanel();
    });

    // A escolha do fluxo (Gerar com IA / Copiar prompt) é feita na etapa
    // Roteiro — ver o handler dos route cards acima.

    // Copiar prompt
    els.promptCopyBtn?.addEventListener('click', async (e) => {
      e.preventDefault();
      try {
        await navigator.clipboard.writeText(buildPrompt(STATE));
        STATE.promptCopied = true;
        const orig = els.promptCopyBtn.innerHTML;
        els.promptCopyBtn.innerHTML = `${svgIcon('check', 14)} Copiado!`;
        els.promptCopyBtn.classList.add('is-copied');
        setTimeout(() => {
          els.promptCopyBtn.innerHTML = orig;
          els.promptCopyBtn.classList.remove('is-copied');
        }, 2000);
      } catch (err) {
        console.warn('[newTrip] clipboard write failed:', err);
      }
    });

    // Render do painel AI. A prévia do prompt agora vive na etapa "Importar"
    // (ela só aparece quando aiFlow === 'prompt', que é exatamente quando a
    // etapa de import existe). O painel fica sempre visível nessa etapa; aqui
    // só mantemos o texto sincronizado com o STATE atual.
    function renderAiPanel() {
      if (!els.promptPreview) return;
      if (STATE.aiFlow === 'prompt') {
        els.promptPreview.textContent = buildPrompt(STATE);
      }
    }

    // ─── Card "Gerar com IA" (etapa Roteiro) — badge de cota ───
    // O card mostra a cota restante (5 gerações grátis por usuário). Até o
    // check assíncrono terminar, fica desabilitado (fail-safe). Ao zerar a
    // cota, desabilita e mostra o link de premium na descrição.
    function renderAiFlowCards() {
      const card = els.aiGenerateCard;
      if (!card) return;
      const q = STATE.aiQuota;
      const checking = q === null;
      const allowed  = !checking && q.allowed === true;

      card.classList.toggle('is-disabled', !allowed);
      card.setAttribute('aria-disabled', allowed ? 'false' : 'true');

      if (els.aiGenerateBadge) {
        const b = els.aiGenerateBadge;
        if (checking) {
          b.textContent = 'Verificando…';
          b.className = 'nt-badge nt-badge--soft';
        } else if (q.unlimited) {
          b.textContent = 'Ilimitado';
          b.className = 'nt-badge nt-badge--accent';
        } else if (q.remaining > 0) {
          b.textContent = `${q.remaining} grátis`;
          b.className = 'nt-badge nt-badge--accent';
        } else {
          b.textContent = 'Premium';
          b.className = 'nt-badge nt-badge--soft';
        }
      }
      if (els.aiGenerateDesc) {
        if (!checking && !allowed) {
          // Cota esgotada — link de plano premium (placeholder, sem destino
          // por ora; o href real entra quando a cobrança existir).
          els.aiGenerateDesc.innerHTML =
            'Você usou suas gerações grátis. '
            + '<a class="nt-premium-link" data-action="premium">Assinar plano premium</a>';
        } else {
          els.aiGenerateDesc.textContent =
            'Geramos um roteiro completo do zero — dias, hotéis e gastos.';
        }
      }

      // Se a cota zerou e o card estava selecionado, limpa a seleção.
      if (!allowed && STATE.routeMode === 'ai' && STATE.aiFlow === 'generate') {
        STATE.routeMode = null;
        STATE.aiFlow = null;
        renderRouteCards();
        renderShell();
      }
    }

    // ─── Etapa Importar ────────────────────────────────────────
    els.importInput?.addEventListener('change', async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      await handleImportFile(file);
    });

    // Drag & drop (UX bonito; o file input já cobre o clique)
    if (els.dropzone) {
      ['dragenter', 'dragover'].forEach((evt) => {
        els.dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          els.dropzone.classList.add('is-dragover');
        });
      });
      ['dragleave', 'drop'].forEach((evt) => {
        els.dropzone.addEventListener(evt, (e) => {
          e.preventDefault();
          els.dropzone.classList.remove('is-dragover');
        });
      });
      els.dropzone.addEventListener('drop', async (e) => {
        e.preventDefault();
        const file = e.dataTransfer?.files?.[0];
        if (!file) return;
        await handleImportFile(file);
      });
    }

    // Botão "Trocar arquivo" (data-action="dz-clear")
    els.stepImport?.addEventListener('click', (e) => {
      const clearBtn = e.target.closest('[data-action="dz-clear"]');
      if (!clearBtn) return;
      e.preventDefault();
      e.stopPropagation();
      resetImport();
    });

    // Toggle "Enviar arquivo" / "Colar JSON" — alterna as duas vias de import.
    els.importModeBtns?.forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const mode = btn.dataset.importMode;
        els.importModeBtns.forEach((b) => b.classList.toggle('is-active', b === btn));
        if (els.importFilePane)  els.importFilePane.hidden  = (mode !== 'file');
        if (els.importPastePane) els.importPastePane.hidden = (mode !== 'paste');
      });
    });
    // "Colar JSON" — processa o texto colado pelo mesmo pipeline do arquivo.
    els.pasteBtn?.addEventListener('click', (e) => {
      e.preventDefault();
      handleImportText(els.pasteInput?.value || '');
    });

    async function handleImportFile(file) {
      if (!file) return;
      const MAX = 1 * 1024 * 1024; // 1 MB
      if (file.size > MAX) {
        setImportError(`Arquivo grande demais (${(file.size / 1024).toFixed(0)} KB). Máximo: 1 MB.`);
        return;
      }
      let text;
      try {
        text = await file.text();
      } catch (err) {
        setImportError(`Falha ao ler o arquivo: ${err?.message || err}`);
        return;
      }
      handleImportText(text, { fileName: file.name, fileSize: file.size });
    }

    // Núcleo do import — parse + validate + convert. Usado pelo upload de
    // arquivo E pelo "Colar JSON" (etapa Importar dividida em duas vias).
    function handleImportText(text, { fileName = '', fileSize = 0 } = {}) {
      STATE.importedFileName = fileName;
      STATE.importedFileSize = fileSize;
      STATE.importError = '';
      STATE.importedData = null;
      STATE.importedInternal = null;
      STATE.importedRaw = text || '';

      function fail(msg) {
        setImportError(msg);
        renderImportDropzone();
        renderImportSummary();
        renderFooter();
      }

      if (!text || !text.trim()) {
        return fail('Cole o JSON gerado pela IA antes de continuar.');
      }

      const parsed = parseAiResponse(text);
      if (!parsed.ok) {
        return fail(parsed.error || 'Não consegui parsear o JSON.');
      }

      const valid = validateAiPayload(parsed.data);
      if (!valid.ok) {
        return fail('Erros de validação:\n• ' + valid.errors.join('\n• '));
      }
      STATE.importedData = parsed.data;

      try {
        STATE.importedInternal = convertToInternal(parsed.data, { defaultCurrency: STATE.currency });
      } catch (err) {
        return fail(`Falha na conversão: ${err?.message || err}`);
      }

      renderImportDropzone();
      renderImportSummary();
      renderFooter();
    }

    function resetImport() {
      STATE.importedFileName = '';
      STATE.importedFileSize = 0;
      STATE.importedRaw = '';
      STATE.importedData = null;
      STATE.importedInternal = null;
      STATE.importError = '';
      if (els.importInput) els.importInput.value = '';
      renderImportDropzone();
      renderImportSummary();
      renderFooter();
    }

    function setImportError(msg) {
      STATE.importError = msg || '';
      if (els.importErrorBox) {
        els.importErrorBox.textContent = msg || '';
        els.importErrorBox.classList.toggle('is-shown', !!msg);
      }
    }

    function renderImportDropzone() {
      if (!els.dzEmpty || !els.dzFile) return;
      const hasFile = !!STATE.importedFileName;
      // Classes em vez do attribute `hidden` (CSS com display:flex sobrescreve)
      els.dzEmpty.classList.toggle('is-shown', !hasFile);
      els.dzFile.classList.toggle('is-shown', hasFile);
      els.dropzone?.classList.toggle('is-filled', hasFile);
      els.dropzone?.classList.toggle('is-error', !!STATE.importError);
      if (hasFile) {
        els.dzFilename.textContent = STATE.importedFileName;
        const sizeKb = (STATE.importedFileSize / 1024).toFixed(1);
        const ok = !!STATE.importedInternal;
        els.dzSize.textContent = ok
          ? `${sizeKb} KB · pronto para importar`
          : `${sizeKb} KB · com problemas — veja abaixo`;
      }
      if (!STATE.importError) setImportError('');
    }

    function renderImportSummary() {
      if (!els.importSummary) return;
      const s = STATE.importedInternal?.summary;
      if (!s) {
        els.importSummary.classList.remove('is-shown');
        els.importSummary.innerHTML = '';
        return;
      }
      els.importSummary.classList.add('is-shown');
      els.importSummary.innerHTML = `
        <p class="eyebrow nt-eyebrow">Pronto pra importar</p>
        <ul class="nt-import-stats">
          <li><strong>${s.days}</strong> dias</li>
          <li><strong>${s.stops}</strong> paradas no roteiro</li>
          <li><strong>${s.stays}</strong> hospedage${s.stays === 1 ? 'm' : 'ns'}</li>
          <li><strong>${s.transports}</strong> transporte${s.transports === 1 ? '' : 's'}</li>
          <li><strong>${s.expenses}</strong> gastos</li>
          ${s.checklist ? `<li><strong>${s.checklist}</strong> itens do checklist</li>` : ''}
        </ul>
      `;
    }

    // ─── Etapa final: Convidados (autocomplete + chips) ────────
    // Usa o componente compartilhado de convite. Aqui o "convite" só
    // adiciona o email à lista de chips — os invites/{token} de fato são
    // criados em lote depois da viagem existir (createInvitesForTrip).
    if (els.guestInviteMount) {
      const guestAc = createUserInviteAutocomplete({
        placeholder: 'Nome ou email do convidado',
        onInvite: async ({ email }) => {
          const e = email.toLowerCase();
          if (STATE.guestEmails.includes(e)) {
            throw new Error('Email já adicionado.');
          }
          STATE.guestEmails.push(e);
          renderGuestChips();
        },
      });
      els.guestInviteMount.appendChild(guestAc.element);
      // Limpa o listener global do componente quando o modal fecha.
      _guestAcCleanup = () => { try { guestAc.destroy(); } catch {} };
    }
    function renderGuestChips() {
      els.guestChips.innerHTML = STATE.guestEmails.map((email, i) => `
        <span class="newtrip-chip">
          <span class="newtrip-chip-icon">${svgIcon('user', 12)}</span>
          <span class="newtrip-chip-text">${esc(email)}</span>
          <button type="button" class="newtrip-chip-remove" data-rm-guest="${i}" aria-label="Remover ${esc(email)}">${svgIcon('close', 11)}</button>
        </span>
      `).join('');
      els.guestChips.querySelectorAll('[data-rm-guest]').forEach((btn) => {
        btn.addEventListener('click', () => {
          STATE.guestEmails.splice(Number(btn.dataset.rmGuest), 1);
          renderGuestChips();
        });
      });
    }

    // ─── Submit ────────────────────────────────────────────────
    // Persiste as cotações da viagem (se o user configurou) em
    // trips/{id}/settings. Best-effort — não derruba a criação da viagem.
    async function persistTripRates(tripId) {
      if (!tripId || !STATE.tripRates || !Object.keys(STATE.tripRates).length) return;
      try {
        await saveTripSettings(tripId, { currencyRates: STATE.tripRates });
      } catch (err) {
        console.warn('[newTrip] saveTripSettings falhou:', err);
      }
    }

    // Puxa as cotações VET da API (câmbio + IOF/spread) e salva na viagem, pra
    // que a carteira já abra com cotação — evita o bug do "gasto em dólar sem
    // cotação". Base = moeda da viagem (STATE.currency), a mesma convenção que a
    // IA usa pras cotações. Best-effort: se a API falhar, a viagem segue sem
    // cotação automática — a carteira ainda faz self-heal ao abrir e o user
    // pode configurar no modal de cotações. NÃO usado no caminho "Gerar com IA":
    // lá o backend já grava as cotações (evita corrida de escrita nos settings).
    async function fetchAndSaveApiRates(tripId) {
      if (!tripId) return;
      try {
        const base = STATE.currency || 'BRL';
        const data = await fetchApiRates(base);
        const vet = data?.vet || {};
        if (Object.keys(vet).length) {
          // API (VET) tem precedência sobre o que a IA/usuário já colocou.
          await saveTripSettings(tripId, { currencyRates: vet });
        }
      } catch (err) {
        console.warn('[newTrip] fetchAndSaveApiRates falhou (segue sem cotação):', err);
      }
    }

    async function submit() {
      if (STATE.submitting) return;

      // Convidados são adicionados como chips via o autocomplete (Enter/clique).
      // STATE.guestEmails já está completo — nada de auto-commit aqui.

      const validation = validate(STATE);
      if (validation) {
        STATE.error = validation;
        showError(els.errorBox, validation);
        return;
      }
      hideError(els.errorBox);

      STATE.submitting = true;
      renderFooter();

      try {
        // Caminho "Gerar com IA": cria a viagem como placeholder
        // (genStatus 'generating'), dispara a geração no backend e fecha o
        // modal NA HORA. A home mostra um card cinza; o backend popula a
        // viagem em background (sobrevive a fechar a aba). Ver createAiGenerateTrip.
        if (STATE.routeMode === 'ai' && STATE.aiFlow === 'generate') {
          const created = await createAiGenerateTrip(STATE);
          await persistTripRates(created?.id);
          toast.success('Viagem criada');
          close(created);
          return;
        }

        // Caminho IA-prompt: importa o JSON enviado pelo user usando o
        // pipeline aiImport (parseAiResponse + validateAiPayload +
        // convertToInternal + cloudSave), sobrescrevendo o tripPayload com os
        // campos comuns do wizard (moeda, places) que o user pode ter ajustado.
        const isAiImport = STATE.routeMode === 'ai'
          && STATE.aiFlow === 'prompt'
          && STATE.importedInternal;

        let result;
        if (isAiImport) {
          // Preserva moeda e places do wizard se o user mudou no Básico
          const internal = STATE.importedInternal;
          internal.tripPayload.currency = STATE.currency || internal.tripPayload.currency;
          internal.tripPayload.aiConfig = buildAiConfig(STATE);
          // Mescla places: usa o que veio do JSON e adiciona os escolhidos no Básico
          const merged = mergePlaces(internal.tripPayload.places, STATE.places);
          internal.tripPayload.places = merged;

          const importRes = await importAiPayload(internal);
          if (importRes.errors?.length) {
            console.warn('[newTrip/ai] import com warnings:', importRes.errors);
          }
          // Cria invites best-effort (igual ao caminho normal)
          const invites = await createInvitesForTrip(importRes.trip, STATE.guestEmails);
          if (invites.length) {
            await showInvitesCreatedModal(invites, importRes.trip);
          }
          result = { trip: importRes.trip, invites };
        } else {
          result = await createTripFromState(STATE);
          if (result.invites?.length) {
            await showInvitesCreatedModal(result.invites, result.trip);
          }
        }
        await persistTripRates(result.trip?.id);
        // Cotações automáticas via API (VET) — só nos caminhos que gravam no
        // cliente. O "Gerar com IA" grava no backend (evita corrida).
        await fetchAndSaveApiRates(result.trip?.id);
        toast.success('Viagem criada');
        close(result.trip);
      } catch (err) {
        STATE.submitting = false;
        console.error('[newTrip] CREATE failed:', err?.code, err?.message, err);
        const msg = friendlyErrorMessage(err);
        STATE.error = msg;
        showError(els.errorBox, msg);
        renderFooter();
      }
    }

    // ─── Renderers ────────────────────────────────────────────
    function renderShell() {
      const total = computeTotalSteps(STATE);
      const labels = computeStepLabels(STATE, total);
      const meta = STEP_META[stepKey(STATE.step, STATE)] || STEP_META.basic;

      // Header
      if (els.eyebrow) els.eyebrow.textContent = `ETAPA ${STATE.step} DE ${total}`;
      if (els.titleH)  els.titleH.textContent  = meta.title;

      // Stepper — cada etapa é um botão clicável. Fica navegável a etapa
      // atual/anteriores e as seguintes cujos predecessores estão TODOS
      // válidos (pular só pra etapa já válida — decisão do usuário).
      els.stepper.innerHTML = labels.map((label, i) => {
        const n = i + 1;
        const state = n < STATE.step ? 'done' : (n === STATE.step ? 'active' : 'pending');
        let navigable = n <= STATE.step;
        if (!navigable) {
          navigable = true;
          for (let k = 1; k < n; k++) {
            if (!isStepValid(STATE, k)) { navigable = false; break; }
          }
        }
        return `
          <button type="button" class="nt-step nt-step--${state}" data-step-index="${n}"
            ${navigable ? '' : 'disabled'}${n === STATE.step ? ' aria-current="step"' : ''}>
            <span class="nt-step-dot">
              ${state === 'done' ? svgIcon('check', 12) : String(n)}
            </span>
            <span class="nt-step-label">${esc(label)}</span>
          </button>
        `;
      }).join('<span class="nt-step-bar" aria-hidden="true"></span>');

      renderFooter();
    }
    function renderFooter() {
      const total = computeTotalSteps(STATE);
      const isLast = STATE.step >= total;
      const valid = isStepValid(STATE, STATE.step);

      // Left button
      if (STATE.step === 1) {
        els.footLeft.innerHTML = '<span>Cancelar</span>';
        els.footLeft.className = 'btn btn-ghost newtrip-foot-back';
      } else {
        els.footLeft.innerHTML = `${svgIcon('arrowL', 14)}<span>Voltar</span>`;
        els.footLeft.className = 'btn btn-ghost newtrip-foot-back';
      }

      // Counter
      els.counter.textContent = `${STATE.step} / ${total}`;

      // Right button
      const ctaLabel = computeCtaLabel(STATE, isLast);
      if (STATE.submitting && isLast) {
        els.footRight.innerHTML = `<span>Criando…</span>`;
        els.footRight.disabled = true;
      } else {
        els.footRight.innerHTML = isLast
          ? `<span>${esc(ctaLabel)}</span>`
          : `<span>${esc(ctaLabel)}</span>${svgIcon('arrowR', 14)}`;
        els.footRight.disabled = !valid;
      }
      els.footRight.className = 'btn btn-primary newtrip-foot-next';
    }
    function renderStepVisibility() {
      const key = stepKey(STATE.step, STATE);
      const map = {
        basic:   els.stepBasic,
        route:   els.stepRoute,
        ai:      els.stepAi,
        aiExtra: els.stepAiExtra,
        import:  els.stepImport,
        invites: els.stepInvites,
      };
      Object.entries(map).forEach(([k, el]) => {
        if (!el) return;
        el.classList.toggle('is-active', k === key);
      });

      // Foco no campo principal da etapa
      if (key === 'basic')   els.title?.focus();
      // A prévia do prompt vive na etapa "Importar" — sincroniza o texto ao
      // entrar nela (e nas etapas de IA, caso o user volte e mude algo).
      if (key === 'ai' || key === 'aiExtra' || key === 'import') renderAiPanel();

      // No fluxo "Copiar prompt" os campos de estilo (ritmo + itinerário)
      // ficam ocultos no aiExtra — gastos e estilo são automáticos.
      if (els.styleFields) {
        els.styleFields.hidden = (STATE.aiFlow === 'prompt');
      }
    }
    function renderRouteCards() {
      // Card ativo: 'ai-generate'/'ai-prompt' quando routeMode==='ai',
      // senão o próprio routeMode ('manual'/'preset').
      const activeKey = STATE.routeMode === 'ai'
        ? `ai-${STATE.aiFlow || ''}`
        : STATE.routeMode;
      els.routeCards.forEach((card) => {
        card.classList.toggle('is-active', card.dataset.routeCard === activeKey);
      });
    }
    function renderRoutePanels() {
      // Preset
      if (els.presetPanel) {
        const show = STATE.routeMode === 'preset';
        els.presetPanel.classList.toggle('is-active', show);

        // Eyebrow do painel — adapta ao destino quando user já tem lugares
        const eyebrow = els.presetPanel.querySelector('[data-slot="preset-eyebrow"]');
        if (eyebrow) {
          const destino = STATE.places[0]?.name || STATE.places[0]?.country || '';
          eyebrow.textContent = destino
            ? `Sugestões para ${destino}`
            : 'Sugestões para você';
        }

        // Lista de presets — renderiza items, loading ou empty state
        const list = els.presetPanel.querySelector('[data-slot="preset-list"]');
        if (list) {
          if (STATE.presetsLoading) {
            list.innerHTML = '<div class="nt-empty">Carregando roteiros…</div>';
          } else if (STATE.presetsError) {
            list.innerHTML = `<div class="nt-empty">${esc(STATE.presetsError)}</div>`;
          } else if (!STATE.presets.length) {
            list.innerHTML = '<div class="nt-empty">Em breve roteiros prontos pra escolher. Por enquanto, comece manual ou pela IA.</div>';
          } else {
            list.innerHTML = STATE.presets.map((p) => renderPresetItem(p, STATE.presetId === p.id)).join('');
          }
        }
      }
    }

    // ─── Hidratação do rascunho restaurado ─────────────────────
    // Inputs de texto não são templados pelo state (o HTML nasce vazio),
    // então setamos os valores aqui. Chips/visibilidade idem. Os campos
    // templados (nº de dias, viajantes, datas) já vieram do MODAL_HTML.
    if (_draft) {
      if (els.title) els.title.value = STATE.title || '';
      if (els.departureCity) els.departureCity.value = STATE.departureCity || '';
      if (els.aiNotes) els.aiNotes.value = STATE.aiNotes || '';
      if (els.alreadyHave) els.alreadyHave.value = STATE.alreadyHave || '';
      renderPlaceChips();
      if (els.numdaysField) {
        els.numdaysField.classList.toggle('is-shown', !!STATE.datesUndecided);
      }
      renderDaysWarn();
    }

    // ─── Auto-save do rascunho ─────────────────────────────────
    // Salva enquanto o usuário edita (debounced), pra não perder nada se o
    // modal fechar sem querer. Cobre inputs/selects via bubbling; mutações
    // não-input (chips de destino, steppers de viajante) chamam saveDraftNow.
    let _saveTimer = null;
    function scheduleDraftSave() {
      clearTimeout(_saveTimer);
      _saveTimer = setTimeout(() => persistNewTripDraft(STATE), 400);
    }
    function saveDraftNow() {
      clearTimeout(_saveTimer);
      persistNewTripDraft(STATE);
    }
    overlay.addEventListener('input', scheduleDraftSave);
    overlay.addEventListener('change', scheduleDraftSave);
    // Exposto pro renderPlaceChips / steppers chamarem após mutar o STATE.
    _scheduleDraftSave = scheduleDraftSave;
    _saveDraftNow = saveDraftNow;

    // ─── Render inicial ────────────────────────────────────────
    renderShell();
    renderStepVisibility();
    renderRouteCards();
    renderRoutePanels();
    renderAiFlowCards();
    setTimeout(() => els.title?.focus(), 30);

    // Check assíncrono da cota de IA. Até resolver, o card "Gerar com IA"
    // fica desabilitado (fail-safe). Quando resolve, habilita/bloqueia.
    (async () => {
      try {
        STATE.aiQuota = await getAiQuota(auth.currentUser?.uid);
      } catch {
        STATE.aiQuota = { used: 0, remaining: 0, unlimited: false, allowed: false };
      }
      renderAiFlowCards();
      renderFooter();
    })();
  });
}

// ====================================================================
//  Presets (Fase 2 stub — plug-and-play Firestore depois)
// ====================================================================

/**
 * Carrega a lista de roteiros pré-prontos.
 *
 * Fase 2 (atual): retorna [] — a UI mostra empty state "Em breve…".
 *
 * Quando a collection `tripPresets` existir no Firestore, substituir
 * o corpo desta função por algo como:
 *
 *   const q = query(collection(db, 'tripPresets'), where('published', '==', true));
 *   const snap = await getDocs(q);
 *   return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
 *
 * Filtros por destino (`places`) podem ser aplicados client-side ou via
 * `where('countries', 'array-contains-any', ...)`.
 */
async function loadPresets(/* { places } = {} */) {
  return [];
}

/** Renderiza um item da lista de presets dentro do sub-painel. */
function renderPresetItem(preset, active) {
  const days  = preset.days ?? preset.numDays;
  const rating = preset.rating;
  const travelers = preset.travelers;
  const meta = [
    days ? `${days} dias` : '',
    rating ? `★ ${rating}` : '',
    travelers ? `${formatNumber(travelers)} viajantes` : '',
  ].filter(Boolean).join(' · ');
  return `
    <button type="button" class="nt-preset-item ${active ? 'is-active' : ''}" data-preset-id="${esc(preset.id)}">
      <span class="nt-preset-thumb">${svgIcon('map', 18)}</span>
      <span class="nt-preset-body">
        <span class="nt-preset-title">${esc(preset.title || preset.name || 'Roteiro')}</span>
        ${preset.description ? `<span class="nt-preset-desc">${esc(preset.description)}</span>` : ''}
        ${meta ? `<span class="nt-preset-meta mono">${esc(meta)}</span>` : ''}
      </span>
      <span class="nt-preset-check" aria-hidden="true">${svgIcon('check', 14)}</span>
    </button>
  `;
}

function formatNumber(n) {
  if (typeof n !== 'number') return String(n);
  return n.toLocaleString('pt-BR');
}

// ====================================================================
//  Etapa AI — enums + prompt builder
// ====================================================================

export const BUDGET_TIERS = [
  { id: 'low',  symbol: '$',    label: 'Econômico' },
  { id: 'mid',  symbol: '$$',   label: 'Equilibrado' },
  { id: 'high', symbol: '$$$',  label: 'Alto' },
  { id: 'lux',  symbol: '$$$$', label: 'Luxo' },
];

// Legenda EXIBIDA no wizard logo abaixo da categoria ativa — texto curto e
// direto sobre o que a categoria significa pra experiência do viajante.
export const BUDGET_TIER_LEGENDS = {
  low:  'Economizar na alimentação, priorizar atrações principais e transportes baratos.',
  mid:  'Equilíbrio entre aproveitar a viagem e não fazer gastos acima da média.',
  high: 'Aproveitar a viagem gastando o necessário e priorizando conforto.',
  lux:  'O céu é o limite, aqui aproveita a viagem sem preocupar com gastos.',
};

export const EXPENSE_DETAIL = {
  category: 'Só por categoria',
  item:     'Detalhado por item',
};

// Legenda exibida abaixo do segmentado de detalhamento (mesma UI da legenda
// das categorias de teto de gastos). Texto cunhado em produto.
export const EXPENSE_DETAIL_LEGENDS = {
  category: 'Gastos separados por alimentação, transporte, estadia, atrações e compras.',
  item:     'Além da visão por categoria, detalhe de gasto na vírgula — como café, passagem e outros.',
};

export const ITINERARY_MODES = [
  { id: 'detailed', title: 'Itinerário detalhado', desc: 'Horários, paradas, custos estimados de cada item' },
  { id: 'basic',    title: 'Itinerário básico',    desc: 'Paradas principais + horários, sem detalhes de custo' },
  { id: 'free',     title: 'Itinerário livre',     desc: 'Só com objetivos do dia (ex.: "explorar centro histórico")' },
];

export const TRAVEL_STYLES = [
  { id: 'tranquila',   label: 'Tranquila',   desc: 'Tudo com calma — folga entre as paradas, chegar ao hotel até as 22h.' },
  { id: 'equilibrada', label: 'Equilibrada', desc: 'De tudo um pouco, sem correria; um dia ou outro pode ser mais puxado.' },
  { id: 'energica',    label: 'Enérgica',    desc: 'Aproveitar cada espaço do roteiro — otimizado pra ver o máximo.' },
];

// ──────────────────────────────────────────────────────────────
// Instruções "tom" — injetadas no prompt conforme a escolha do user.
// Documentadas em .claude/projects/newtrip/ai-prompt-schema.md (v1.1).
// ──────────────────────────────────────────────────────────────
// Instruções enviadas no prompt — descrevem o comportamento esperado da IA
// pra cada tier de gasto. Texto cunhado em produto (feedback de clientes).
const BUDGET_TIER_INSTRUCTIONS = {
  low:  'Econômico. Gastar pouco, priorizar restaurantes mais baratos e comida de mercado, atrações somente algumas principais sem gastar muito, transportes os mais baratos.',
  mid:  'Equilibrado. Balancear alimentações entre restaurantes e lugares mais baratos, adicionar atrações principais e sugestões baratas, transportes equilibrar conforto e preço.',
  high: 'Alto. Aproveitar todas as atrações que cabem no roteiro e colocar um extra ou outro, alimentação majoritariamente em restaurantes, transportes priorizar o conforto.',
  lux:  'Luxo. Sem teto de gastos, coloque gastos dos melhores restaurantes, melhores atrações e com transportes da melhor qualidade.',
};

const ITINERARY_MODE_INSTRUCTIONS = {
  detailed: 'Roteiro DETALHADO: planeje cada hora desde acordar até dormir. Toda parada tem `time` (HH:MM). Algumas paradas podem durar 2-3h (museu, almoço, atração principal). Inclua pausas pra café/descanso.',
  basic:    'Roteiro BÁSICO: divida cada dia em 3 blocos — Manhã, Tarde, Noite. Cada bloco com 1-2 paradas principais. `time` opcional (pode ser null), mas marque o período (manhã ~09:00, tarde ~14:00, noite ~19:00).',
  free:     'Roteiro LIVRE: SÓ a atração ou tema principal do dia. 1 parada por dia (no máximo 2). Sem horários precisos (`time: null`). Foco no objetivo geral do dia.',
};

const EXPENSE_DETAIL_INSTRUCTIONS = {
  category: 'O usuário prefere ver gastos agrupados por categoria. Você ainda deve preencher `cost` em stops/stays/transports principais (refeições, hotéis, transportes inter-cidades) com `category` correta — o app vai agrupar por categoria na carteira. Pode omitir `cost` (deixar `null`) em paradas pequenas/baratas pra evitar poluir a lista.',
  item:     'O usuário quer ver gastos item-por-item. Preencha `cost` em TODAS as stops/stays/transports sempre que houver gasto, mesmo pequenos (café, sorvete, ingresso simples). Categoria sempre explícita.',
};

const TRAVEL_STYLE_INSTRUCTIONS = {
  tranquila:   'Ritmo TRANQUILO: faça tudo com calma. Deixe folgas de tempo entre as paradas — não lote os dias. Planeje pra o viajante chegar ao hotel até as 22h. Menos paradas por dia, com respiro.',
  equilibrada: 'Ritmo EQUILIBRADO: faça de tudo um pouco, mas de forma calma, sem correria. É aceitável um dia ou outro mais puxado quando vale a pena — mas o padrão é confortável.',
  energica:    'Ritmo ENÉRGICO: aproveite cada espaço do roteiro. Otimize os dias pra ver o máximo possível — pode encadear mais paradas, começar cedo e terminar tarde.',
};

/**
 * Gera o prompt completo que o user vai colar na IA favorita.
 *
 * Versão v1.2 — alinhada com `.claude/projects/newtrip/ai-prompt-schema.md`.
 * Mudanças vs v1.1 (depois de teste real com Claude):
 *   - `cost` agora tem `{amount, currency, category}` — categoria EXPLÍCITA
 *     em cada gasto, eliminando inferência via `type` (que pode errar:
 *     ex.: "drink no pôr do sol" com type:viewpoint mas categoria food)
 *   - Removido `expenses.summary` (parser calcula via carteira; é só ruído)
 *   - Reforçado o papel de `expenses.misc[]`: SÓ avulsos legítimos
 *     (gorjeta, SIM, lembrancinha, taxa) — não duplicar costs
 *
 * Mudanças vs v0.1:
 *   - Custos têm formato `{amount, currency, category}` (multi-moeda)
 *   - Tom dinâmico: instruções específicas por budgetTier/itineraryMode/expenseDetail
 *   - Removidos boat e metro do enum de transport (app não suporta)
 *
 * Estratégia:
 *   - Cabeçalho com dados estruturados
 *   - Instrução EXPLÍCITA pra responder só com bloco ```json``` (mitiga
 *     narrativa extra dos modelos, especialmente Gemini)
 *   - Schema inline com tipos + exemplos curtos
 *   - Enums separados pra evitar invenção de tipos
 *   - Tom calibrado pela escolha do user (budgetTier, itineraryMode, expenseDetail)
 */
export function buildPrompt(state) {
  const tierLabel   = BUDGET_TIERS.find((t) => t.id === state.budgetTier)?.label || '—';
  const detailLabel = EXPENSE_DETAIL[state.expenseDetail] || '—';
  const modeLabel   = ITINERARY_MODES.find((m) => m.id === state.itineraryMode)?.title || '—';

  const tierInstr   = BUDGET_TIER_INSTRUCTIONS[state.budgetTier] || '';
  const modeInstr   = ITINERARY_MODE_INSTRUCTIONS[state.itineraryMode] || '';
  const detailInstr = EXPENSE_DETAIL_INSTRUCTIONS[state.expenseDetail] || '';

  const travelLabel = TRAVEL_STYLES.find((s) => s.id === state.travelStyle)?.label || '—';
  const travelInstr = TRAVEL_STYLE_INSTRUCTIONS[state.travelStyle] || '';

  // Bloco de gastos do prompt. Três casos:
  //   1. expensesUndefined → IA não estima nada.
  //   2. budgetMode === 'amount' → IA respeita um TETO em BRL (distribui entre categorias).
  //   3. budgetMode === 'category' (default) → IA segue o perfil da categoria.
  let gastosSection;
  if (state.expensesUndefined) {
    gastosSection = '**Gastos**: NÃO definir custos agora.\n'
      + 'O usuário ainda não quer estimar gastos. Em TODOS os campos de custo '
      + '(`cost`, `price_per_night`, `total_cost` e quaisquer outros) use `null`, '
      + 'e deixe `expenses.misc` como lista vazia `[]`. Esta regra tem PRIORIDADE: '
      + 'ignore qualquer outra instrução deste prompt sobre estimar valores, '
      + 'INCLUSIVE a de passagem aérea. Foque só no roteiro — dias, paradas, '
      + 'hospedagens e transportes — sem números de dinheiro.';
  } else if (state.budgetMode === 'amount' && Number.isFinite(state.budgetAmount) && state.budgetAmount > 0) {
    const valorFmt = state.budgetAmount.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    gastosSection = `**Orçamento TOTAL da viagem**: R$ ${valorFmt}\n`
      + `Distribua esse teto entre TRANSPORTE (passagens + transfers), HOSPEDAGEM, ALIMENTAÇÃO e ATRAÇÕES de modo realista, somando ao final um valor próximo desse total (com 5–10% de margem). Use proporções razoáveis pra rota (voos internacionais costumam ser 30–45% do orçamento, hospedagem 25–35%, alimentação 15–25%, atrações 10–20%). Se o teto for incompatível com o destino (ex.: R$ 2.000 pra 7 dias na Suíça), ainda assim respeite o teto — corte hospedagens caras, reduza atrações pagas e aponte na \`trip.summary\` que o orçamento ficou apertado.\n\n`
      + `**Detalhamento dos gastos**: ${detailLabel}\n${detailInstr}`;
  } else {
    gastosSection = `**Teto de gastos**: ${tierLabel}\n${tierInstr}\n\n`
      + `**Detalhamento dos gastos**: ${detailLabel}\n${detailInstr}`;
  }

  const destinos = state.places.length
    ? state.places.map((p) => p.country ? `${p.name}, ${p.country}` : p.name).join('; ')
    : '— (a definir)';

  const alreadyHave = (state.alreadyHave || '').trim();

  let periodo;
  if (state.datesUndecided) {
    periodo = state.numDays ? `${state.numDays} dias (datas a definir)` : 'A definir';
  } else if (state.start && state.end) {
    periodo = `${state.start} a ${state.end}`;
  } else {
    periodo = 'A definir';
  }

  return `Você é um planejador de viagens experiente. Vou te passar os dados de uma viagem e quero o roteiro COMPLETO em JSON estruturado pra importar num app de planejamento.

## Dados da viagem
Título: ${state.title.trim() || '—'}
Período: ${periodo}
Destinos: ${destinos}
Cidade de partida: ${state.departureCity.trim() || '— (a definir)'}
Viajantes: ${state.adults || 1} adulto(s)${state.children > 0 ? `, ${state.children} criança(s)` : ''}
O que o viajante já tem comprado/reservado: ${alreadyHave || '—'}
Observações do viajante: ${state.aiNotes.trim() || '—'}

## Estilo desejado

**Ritmo da viagem**: ${travelLabel}
${travelInstr}

**Estilo do itinerário**: ${modeLabel}
${modeInstr}

${gastosSection}

## Roteiro enxuto (IMPORTANTE)
Este roteiro deve ser SIMPLES e direto — fácil de gerar e de revisar:
- No MÁXIMO 3 paradas por dia em \`days[].stops[]\` (foque nas atrações e refeições principais do dia). Esta regra tem PRIORIDADE sobre o estilo do itinerário.
- Horários são opcionais: pode deixar \`time: null\`. Não detalhe o dia minuto a minuto.
- Em \`transports[]\`, inclua APENAS deslocamentos de longa distância — voos e trajetos que trocam de cidade. NÃO inclua transfers locais, metrô, ônibus urbano nem táxi dentro da cidade.
- Omita campos opcionais que você não souber preencher — não invente dados só pra completar.

## Como responder

Responda APENAS com um bloco \`\`\`json\`\`\` válido. Não escreva nada antes ou depois — SÓ o JSON dentro do bloco.

Regras:
- Datas em ISO 8601 (YYYY-MM-DD); horários em 24h (HH:MM).
- **MOEDA (CRÍTICO)** — você DEFINE a moeda da viagem com base no destino e o app usa isso pra cotação:
  · \`trip.currency\` = moeda principal do destino (EUR pra Europa, USD pra EUA, GBP pra Reino Unido, JPY pra Japão, ARS pra Argentina, MXN pra México, etc). Se for viagem dentro do Brasil, use BRL.
  · \`trip.currency_rates\` = OBJETO com a cotação ATUAL aproximada de cada moeda usada na viagem em **reais (BRL)**. Ex.: \`{"EUR": 5.85, "USD": 5.40, "CHF": 6.20}\`. Use os valores mais recentes que você conhece. O app salva isso automaticamente nas configurações da viagem.
  · Em cada \`cost.currency\` use a moeda do PAÍS onde o gasto acontece: restaurante em Zurique → CHF; ingresso em Tóquio → JPY; lanche no aeroporto de Guarulhos → BRL.
  · **EXCEÇÕES — SEMPRE em BRL**: passagens aéreas (\`transports[].type === 'flight'\`) e hospedagens (\`stays[].price_per_night\` e \`stays[].total_cost\`). Voos e hotéis costumam ser comprados no Brasil em real — manter em BRL.
  · Outros transportes (trem, ônibus, carro alugado, transfer) seguem a moeda LOCAL do trecho.
- Cada custo tem \`{amount, currency, category}\`. Categoria EXPLÍCITA em todo cost (o app organiza a carteira por categoria).
- Categorias válidas: \`food | lodging | transport | attractions | shopping | misc\`.
- Idioma pt-BR nos textos. Decimais com ponto (\`220.50\`).
- Use \`null\` pro cost de paradas grátis/inclusas. Não use 0 — ambíguo.
- **VINCULAÇÃO de gastos (CRÍTICO)**: gastos de HOSPEDAGEM vão SEMPRE em \`stays[].total_cost\` (ou \`price_per_night\`), NUNCA em \`expenses.misc[]\` ou \`stops[].cost\`. Gastos de TRANSPORTE (voo, trem, ônibus, transfer, táxi inter-cidade) vão SEMPRE em \`transports[].cost\`, NUNCA em \`expenses.misc[]\` ou \`stops[].cost\`. Isso garante que o app vincule cada gasto ao hotel/rota correta na carteira (você verá um ícone de "linkado" e poderá clicar pra ir direto ao hotel/transporte). Gastos em misc viram ÓRFÃOS — só use misc pra avulsos genuínos que NÃO correspondem a hotel/transporte/parada.
- \`expenses.misc[]\` é APENAS pra gastos avulsos que não se encaixam num stop, stay ou transport: gorjetas, SIM, lembranças, seguros, taxa de praia, lavanderia, lavar roupa, vacina, visto. Cost de paradas vai em \`stops[].cost\`; cost de hotel vai em \`stays[].total_cost\`; cost de transporte vai em \`transports[].cost\`. NÃO duplique.
- **PASSAGEM AÉREA — SEMPRE ESTIME**: **SEMPRE estime um custo realista da passagem aérea baseado em rota e época do ano**, mesmo que o usuário não tenha informado. Use valores médios de mercado em reais. **Não deixe \`amount\` como null** em transportes aéreos (flight). Baseline 2026 (classe econômica, ida e volta quando aplicável): SP→Madrid ~R$ 4500-6500; SP→Lisboa ~R$ 4000-6000; SP→NYC ~R$ 5000-7500; SP→Buenos Aires ~R$ 1800-3200; SP→Santiago ~R$ 2200-3800; doméstico curta (SP↔RIO/BH/CWB) ~R$ 600-1200; doméstico longa (SP↔SSA/REC/FOR/MAO) ~R$ 900-1800. Ajuste pra alta temporada (julho/dezembro/janeiro/feriadões +20-40%). Marque \`estimated: true\` no item de transporte aéreo quando estiver estimando — o app sinaliza pro user que o valor é estimativa de mercado e ele pode ajustar quando comprar a passagem real. Para outros transportes sem preço informado (trens internacionais caros, cruzeiros) sem baseline confiável, \`null\` é aceitável. Para transportes locais (metrô, ônibus urbano, transfer aeroporto), use estimativa razoável normal.
- Sempre 1-indexe \`days[].n\` (1, 2, 3…); use o mesmo \`n\` em \`stays[].day_from/day_to\` e \`transports[].day\`.

## Regras importantes sobre LOCALIZAÇÃO das paradas (\`days[].stops[]\`)

Cada \`stop\` que tem \`lat\`/\`lng\`/\`location\` vira um **pin no mapa**. Só anexe coordenadas a paradas que correspondem a um **lugar físico real onde o viajante precisa chegar**. Caso contrário, deixe \`lat: null\`, \`lng: null\` e \`location: null\`.

- **COM localização** (preencha \`lat\`/\`lng\`/\`location\`): atrações, pontos turísticos, museus, mirantes, restaurantes, cafés, lojas, parques, atividades — qualquer lugar distinto que o viajante visita e precisa navegar até lá.
- **Check-in e check-out do hotel** (\`type: hotel_checkin\` / \`hotel_checkout\`): ESTES recebem a localização do hotel (mesma \`lat\`/\`lng\`/\`address\` que você usou no \`stays[]\` correspondente) — eles abrem o cartão do hotel no mapa.
- **SEM localização** (use \`lat: null\`, \`lng: null\`, \`location: null\`): ações internas ou genéricas ligadas ao hotel que NÃO são check-in/check-out — "descansar no hotel", "tempo livre no hotel", "café da manhã no hotel", "arrumar as malas", "relaxar na piscina do hotel". O hotel já tem o próprio cartão (via \`stays[]\`); não crie um pin separado pra essas paradas.
- **SEM localização** também pra qualquer outra parada que não corresponda a um lugar físico pra navegar: "dia livre", "acordar cedo", "fazer as compras de última hora" sem loja definida, deslocamentos internos genéricos. Na dúvida, se não dá pra apontar o dedo num ponto exato do mapa, deixe sem coordenadas.

## Regras importantes sobre HOSPEDAGENS (\`stays[]\`)

**TODO DIA DA VIAGEM PRECISA TER HOSPEDAGEM** — não deixe nenhum dia sem hotel/airbnb/etc.
- Cubra TODOS os \`days[].n\` com algum elemento em \`stays[]\` usando \`day_from\`/\`day_to\` consecutivos.
- Se houver mudança de cidade ou hotel no meio da viagem, crie múltiplos \`stays[]\` com períodos consecutivos (ex.: stay A de day_from=1 a day_to=3, stay B de day_from=3 a day_to=5).
- Inclua sempre \`address\`, \`lat\`, \`lng\` quando possível pro mapa funcionar.

## Tamanho do grupo

A viagem é pra **${state.adults || 1} adulto(s)${state.children > 0 ? ` e ${state.children} criança(s)` : ''}**. Ajuste:
- Preços de hospedagem e refeições conforme o grupo (quartos duplos/triplos, mesas, ingressos por pessoa).
- Atrações apropriadas (se houver crianças: parques, atividades family-friendly; sem crianças: bares, vida noturna OK).
- Ritmo do roteiro (com crianças: menos paradas por dia, intervalos maiores; sem crianças: pode ser mais intenso).

## Regras importantes sobre TRANSPORTES (\`transports[]\`)

INCLUA SEMPRE:
1. **Transporte da cidade de partida pro primeiro destino** (day 1): voo, trem ou ônibus saindo de \`${state.departureCity.trim() || '<cidade de partida>'}\` pra primeira cidade do roteiro.
2. **Transporte de volta no último dia**: do último destino pra \`${state.departureCity.trim() || '<cidade de partida>'}\`.
3. **Transfer aeroporto/rodoviária ↔ hotel** quando aplicável (carro/uber/transfer), com horário consistente com o pouso/embarque.
4. **Deslocamentos entre cidades** dentro da viagem (trem, ônibus, balsa, carro).

**SEMPRE preencha as coordenadas em \`transports[]\`** (\`from_lat\`, \`from_lng\`, \`to_lat\`, \`to_lng\`) — use coordenadas reais de aeroportos famosos (GRU, GIG, CNF, REC, BSB, ZRH, JFK, etc), cidades grandes, ou hotéis que você listou em \`stays[]\`. Se não souber a coordenada exata de um ponto, use a coordenada da cidade. NUNCA deixe coordenada de transport como null/zero — sem coords o app desenha a rota no oceano africano.

## Schema
\`\`\`json
{
  "trip": {
    "title": "string",
    "currency": "moeda principal do destino (ex: CHF, EUR, USD, JPY, BRL...)",
    "currency_rates": { "EUR": 5.85, "USD": 5.40, "CHF": 6.20 },
    "start_date": "YYYY-MM-DD | null",
    "end_date": "YYYY-MM-DD | null",
    "departure_city": "string | null",
    "summary": "1-3 frases descrevendo o roteiro"
  },
  "days": [
    {
      "n": 1,
      "date": "YYYY-MM-DD | null",
      "city": "Zurique",
      "country": "Suíça",
      "title": "Chegada e Old Town",
      "summary": "Resumo do dia em 1 frase",
      "stops": [
        {
          "time": "09:00",
          "title": "Café da manhã no hotel",
          "type": "restaurant",
          "duration_min": 45,
          "location": "Hotel Storchen, Weinplatz 2, Zürich",
          "lat": 47.3712,
          "lng": 8.5413,
          "cost": { "amount": 25, "currency": "CHF", "category": "food" },
          "notes": null
        }
      ]
    }
  ],
  "stays": [
    {
      "day_from": 1,
      "day_to": 3,
      "name": "Hotel Storchen",
      "address": "Weinplatz 2, 8001 Zürich",
      "city": "Zurique",
      "type": "hotel",
      "lat": 47.3712,
      "lng": 8.5413,
      "price_per_night": { "amount": 220, "currency": "CHF", "category": "lodging" },
      "total_cost":      { "amount": 660, "currency": "CHF", "category": "lodging" },
      "notes": null
    }
  ],
  "transports": [
    {
      "day": 1,
      "from": "São Paulo (GRU)",
      "to": "Zurique (ZRH)",
      "from_lat": -23.4356, "from_lng": -46.4731,
      "to_lat":   47.4647,  "to_lng":   8.5492,
      "type": "flight",
      "depart_date": "2026-08-09",
      "depart_time": "23:55",
      "arrive_date": "2026-08-10",
      "arrive_time": "15:30",
      "carrier": "LATAM 8088",
      "cost": { "amount": 5800, "currency": "BRL", "category": "transport" },
      "estimated": true,
      "duration_min": 760,
      "notes": "Estimativa de mercado — voo internacional SP→Zurique econômica em 2026."
    }
  ],
  "expenses": {
    "misc": [
      { "day": 2, "desc": "SIM eSIM 10GB",        "amount": 25,  "currency": "EUR", "category": "misc" },
      { "day": 2, "desc": "Gorjeta bugueiro",     "amount": 50,  "currency": "BRL", "category": "transport" },
      { "day": null, "desc": "Seguro viagem",     "amount": 180, "currency": "BRL", "category": "misc" }
    ]
  },
  "checklist": {
    "documents":  ["Passaporte válido por +6 meses", "Visto Schengen"],
    "todo_before": ["Reservar hotel em Lucerna", "Comprar SIM eSIM"]
  }
}
\`\`\`

### Enums
- \`days[].stops[].type\`: restaurant | cafe | attraction | museum | viewpoint | shopping | activity | break | hotel_checkin | hotel_checkout | transport | misc
- \`stays[].type\`: hotel | airbnb | pousada | hostel | apartment | cruise | outro
- \`transports[].type\`: flight | train | bus | car | walk | cable_car
- \`cost.category\` e \`expenses.misc[].category\`: food | lodging | transport | attractions | shopping | misc

### Como escolher \`category\` (NÃO use o \`type\` da stop como atalho)
- Pense no que o user paga, não no tipo da atividade.
- Restaurante, café, drink, sorvete, água, lanche → \`food\`
- Hotel, hostel, airbnb, pousada, taxa do quarto → \`lodging\`
- Voo, trem, ônibus, táxi/Uber, transfer, combustível, pedágio, gorjeta de bugueiro → \`transport\`
- Museu, tour, passeio de barco, ingresso de atração, mergulho → \`attractions\`
- Compras, souvenirs, roupa, artesanato → \`shopping\`
- Tudo que não encaixa: seguro, SIM/eSIM, lavanderia, taxas avulsas → \`misc\`

Se algum campo não fizer sentido, use \`null\`. Não invente chaves além das do schema.`;
}

/**
 * Extrai do STATE os parâmetros estruturados que o backend usa pra gerar a
 * viagem em chunks (/api/ai/generate-trip). É o que vai no corpo da
 * requisição — o backend monta os prompts (esqueleto + dias) a partir disto,
 * em vez de receber um prompt pronto.
 */
export function aiGenerationParams(state) {
  return {
    title: (state.title || '').trim(),
    places: (state.places || []).map((p) => ({ name: p.name, country: p.country || '' })),
    departureCity: (state.departureCity || '').trim(),
    datesUndecided: !!state.datesUndecided,
    start: state.start || '',
    end: state.end || '',
    numDays: Number(state.numDays) || null,
    adults: Number(state.adults) || 1,
    children: Number(state.children) || 0,
    currency: state.currency || 'BRL',
    budgetMode:   state.budgetMode || 'category',
    budgetAmount: Number.isFinite(state.budgetAmount) && state.budgetAmount > 0
      ? state.budgetAmount
      : null,
    budgetTier:    state.budgetTier || 'mid',
    expenseDetail: state.expenseDetail || 'category',
    itineraryMode: state.itineraryMode || 'detailed',
    travelStyle:   state.travelStyle || 'equilibrada',
    expensesUndefined: !!state.expensesUndefined,
    alreadyHave: (state.alreadyHave || '').trim(),
    notes:       (state.aiNotes || '').trim(),
  };
}

// ====================================================================
//  Steps — metadata, validação, totalização
// ====================================================================

const STEP_META = {
  basic:   { title: 'Nova viagem',                  label: 'Básico' },
  route:   { title: 'Como vamos montar o roteiro?', label: 'Roteiro' },
  ai:      { title: 'Gastos da viagem',             label: 'Gastos' },
  aiExtra: { title: 'Detalhes para a IA',           label: 'Detalhes' },
  import:  { title: 'Importar arquivo gerado',      label: 'Importar' },
  invites: { title: 'Convidar viajantes',           label: 'Convidados' },
};

/**
 * Sequência ordenada de step-keys ativos pro STATE atual.
 *
 * A etapa "Convidados" foi removida do wizard — convites passam a ser feitos
 * depois, já dentro da viagem. O fluxo 'generate' termina em 'aiExtra': ao
 * confirmar, a viagem é criada como placeholder e a geração roda em background.
 */
function stepSequence(state) {
  const seq = ['basic', 'route'];
  if (state.routeMode === 'ai') {
    if (state.aiFlow === 'generate') {
      seq.push('ai', 'aiExtra');
    } else if (state.aiFlow === 'prompt') {
      // "Copiar prompt" pula a etapa de Gastos — gastos e estilo são
      // automáticos (roteiro mais simples).
      seq.push('aiExtra', 'import');
    }
  }
  return seq;
}

function stepKey(stepNum, state) {
  const seq = stepSequence(state);
  return seq[stepNum - 1] || 'basic';
}

function computeTotalSteps(state) {
  return stepSequence(state).length;
}

function computeStepLabels(state, total) {
  return stepSequence(state).slice(0, total).map((k) => STEP_META[k].label);
}

function computeCtaLabel(state, isLast) {
  if (!isLast) return 'Avançar';
  // 'generate': a última etapa é 'aiExtra' — confirmar dispara a geração.
  if (state.routeMode === 'ai' && state.aiFlow === 'generate') {
    return 'Gerar viagem com IA';
  }
  // 'prompt': a última etapa é 'import' — confirmar importa o JSON e cria.
  if (state.routeMode === 'ai' && state.aiFlow === 'prompt') {
    return 'Importar e criar viagem';
  }
  return 'Criar viagem';
}

function isStepValid(state, stepNum) {
  const key = stepKey(stepNum, state);
  if (key === 'basic') {
    if (!state.title || state.title.trim().length < 3) return false;
    // Cidade de partida é obrigatória (usada pra calcular transporte de
    // ida/volta e dar contexto à IA).
    if (!state.departureCity || !state.departureCity.trim()) return false;
    // Pelo menos 1 destino é obrigatório (o app precisa de destino pra plotar
    // mapa, gerar prompt da IA com sentido, etc).
    if (!Array.isArray(state.places) || state.places.length < 1) return false;
    if (state.datesUndecided) {
      // Sem datas, exige número de dias selecionado (>= 1).
      if (!Number.isFinite(state.numDays) || state.numDays < 1) return false;
    } else if (state.start && state.end && state.start > state.end) {
      return false;
    }
    // Teto de duração — defesa extra (os inputs já capam em MAX_TRIP_DAYS).
    if (effectiveTripDays(state) > MAX_TRIP_DAYS) return false;
    return true;
  }
  if (key === 'route') {
    if (!state.routeMode) return false;
    if (!ROUTE_MODES_FUNCTIONAL.has(state.routeMode)) return false;
    // 'preset' precisa de um presetId selecionado (lista pode estar vazia
    // → empty state → user não consegue avançar, exatamente o esperado).
    if (state.routeMode === 'preset' && !state.presetId) return false;
    if (state.routeMode === 'ai') {
      if (!state.aiFlow) return false;
      // 'generate' exige cota de IA disponível (o card já fica desabilitado
      // sem cota; isto é a trava de segurança).
      if (state.aiFlow === 'generate' && state.aiQuota?.allowed !== true) return false;
    }
    return true;
  }
  if (key === 'ai') {
    // Teto de gastos / detalhamento / estilo têm defaults — sempre válida.
    return true;
  }
  if (key === 'aiExtra') {
    // O fluxo (gerar/copiar) já foi escolhido na etapa Roteiro; aqui os
    // campos são todos opcionais.
    return true;
  }
  if (key === 'import') {
    // Avançar (= importar e criar) só com o JSON validado e convertido.
    return !!(state.importedInternal && !state.importError);
  }
  if (key === 'invites') {
    return true; // sempre opcional
  }
  return false;
}

function validate(state) {
  if (!state.title.trim()) return 'Dê um nome pra sua viagem.';
  if (state.title.trim().length < 3) return 'O título precisa ter pelo menos 3 caracteres.';
  if (!state.departureCity || !state.departureCity.trim()) {
    return 'Informe a cidade de partida.';
  }
  if (!Array.isArray(state.places) || state.places.length < 1) {
    return 'Adicione pelo menos 1 destino no campo "Destinos".';
  }
  if (state.datesUndecided) {
    if (!Number.isFinite(state.numDays) || state.numDays < 1) {
      return 'Informe quantos dias a viagem terá (use o slider abaixo das datas).';
    }
  } else if (state.start && state.end && state.start > state.end) {
    return 'Data de fim antes da data de início.';
  }
  if (effectiveTripDays(state) > MAX_TRIP_DAYS) {
    return `O limite é de ${MAX_TRIP_DAYS} dias por viagem.`;
  }
  if (!state.routeMode) return 'Escolha como montar o roteiro.';
  if (state.routeMode === 'preset' && !state.presetId) {
    return 'Roteiros existentes chegam em breve. Selecione outra opção por enquanto.';
  }
  return null;
}

// ====================================================================
//  Create flow
// ====================================================================

async function createTripFromState(state) {
  const tripsApi = window.tripsApi;
  if (!tripsApi || typeof tripsApi.create !== 'function') {
    throw new Error('Sistema de viagens indisponível. Tente recarregar a página.');
  }
  const adminEmail = auth.currentUser?.email || null;

  // `state.start` é ISO "YYYY-MM-DD" vindo do tripDateRange (flatpickr).
  // O resto do app (home.js, tripMeta, roteiro, expenses…) espera `trip.year`
  // pra parsear datas DD/MM; sem isso cai no fallback `new Date().getFullYear()`
  // e a viagem de 2027 passa a ser tratada como ano atual. Por isso derivamos
  // o ano AQUI, a partir do ISO — não pode ficar undefined.
  const startIso = state.datesUndecided ? '' : (state.start || '');
  const yearMatch = startIso.match(/^(\d{4})-/);

  const tripPayload = {
    name: state.title.trim(),
    startDate: startIso,
    endDate:   state.datesUndecided ? '' : (state.end   || ''),
    year: yearMatch ? Number(yearMatch[1]) : null,
    datesUndefined: !!state.datesUndecided,
    currency: state.currency || 'BRL',
    places: state.places.map((p) => ({
      name: p.name,
      country: p.country || '',
      lat: p.lat ?? null,
      lng: p.lng ?? null,
      placeId: p.placeId || '',
    })),
    // Marcadores de método de criação — úteis pra futura UI / telemetria.
    routeMode: state.routeMode || 'manual',
    presetId:  state.routeMode === 'preset' ? (state.presetId || null) : null,
    // Quando o usuário escolheu IA + "Copiar prompt", a viagem fica
    // marcada como aguardando importação. O menu da viagem pode oferecer
    // um botão "Importar JSON da IA" pra essas trips.
    awaitingImport: state.routeMode === 'ai' && state.aiFlow === 'prompt',
    aiConfig: buildAiConfig(state),
  };

  console.info('[newTrip] payload →', tripPayload);
  let created;
  try {
    created = await tripsApi.create(tripPayload);
  } catch (err) {
    console.error('[newTrip] tripsApi.create failed:', err?.code, err?.message, err);
    throw err;
  }
  if (!created?.id) throw new Error('Viagem criada sem ID — checa a console.');
  console.info('[newTrip] trip created:', created.id);

  const invites = await createInvitesForTrip(created, state.guestEmails);
  return { trip: created, invites };
}

/**
 * Fluxo "Gerar com IA": cria a viagem como PLACEHOLDER (genStatus
 * 'generating') e dispara a geração no backend. O endpoint /api/ai/generate-trip
 * responde 202 na hora e segue gerando em background — sobrevive mesmo se o
 * usuário fechar a aba.
 *
 * Retorna a viagem criada (com genStatus). Se o disparo falhar, marca a
 * viagem como 'failed' pra ela não ficar eternamente cinza na home.
 */
async function createAiGenerateTrip(state) {
  const tripsApi = window.tripsApi;
  if (!tripsApi || typeof tripsApi.create !== 'function') {
    throw new Error('Sistema de viagens indisponível. Tente recarregar a página.');
  }

  // Mesmo derive de ano do createTripFromState — `trip.year` é crítico pro
  // resto do app parsear datas DD/MM.
  const startIso = state.datesUndecided ? '' : (state.start || '');
  const yearMatch = startIso.match(/^(\d{4})-/);

  const tripPayload = {
    name: state.title.trim(),
    startDate: startIso,
    endDate:   state.datesUndecided ? '' : (state.end || ''),
    year: yearMatch ? Number(yearMatch[1]) : null,
    datesUndefined: !!state.datesUndecided,
    currency: state.currency || 'BRL',
    places: state.places.map((p) => ({
      name: p.name,
      country: p.country || '',
      lat: p.lat ?? null,
      lng: p.lng ?? null,
      placeId: p.placeId || '',
    })),
    routeMode: 'ai',
    presetId: null,
    awaitingImport: false,
    aiConfig: buildAiConfig(state),
    // Status da geração — a home usa pra mostrar o card cinza; o backend
    // atualiza pra 'ready' (ou 'failed') quando termina.
    genStatus: 'generating',
    genStartedAt: new Date(),
  };

  console.info('[newTrip/ai] criando placeholder →', tripPayload.name);
  const created = await tripsApi.create(tripPayload);
  if (!created?.id) throw new Error('Viagem criada sem ID — checa a console.');

  // Dispara a geração no backend. 202 = aceito (segue em background).
  // Disparo falhou → marca a viagem como 'failed'.
  try {
    const res = await authFetch(API.aiGenerateTrip(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        tripId: created.id,
        params: aiGenerationParams(state),
      }),
    });
    if (!res.ok) {
      const d = await res.json().catch(() => ({}));
      throw new Error(d?.error || `o servidor respondeu ${res.status}`);
    }
  } catch (err) {
    console.error('[newTrip/ai] disparo da geração falhou:', err);
    try {
      await updateTrip(created.id, {
        genStatus: 'failed',
        genError: 'Não foi possível iniciar a geração: ' + (err?.message || err),
      });
    } catch (e2) {
      console.warn('[newTrip/ai] não consegui marcar a viagem como failed:', e2);
    }
    created.genStatus = 'failed';
  }

  return created;
}

/**
 * Cria invites de e-mail pra uma trip JÁ criada. Reutilizado tanto pelo
 * caminho manual quanto pelo caminho IA-import. Best-effort: falhas
 * individuais não derrubam o save geral.
 */
async function createInvitesForTrip(trip, emails) {
  const invites = [];
  if (!trip?.id || !Array.isArray(emails) || !emails.length) return invites;
  const adminEmail = auth.currentUser?.email || null;

  for (const email of emails) {
    try {
      const token = _randomToken();
      await setDoc(doc(db, 'invites', token), {
        tripId: trip.id,
        createdBy: auth.currentUser.uid,
        createdByEmail: adminEmail,
        email,                    // convite amarrado a este endereço
        guestEmail: email,        // compat com campo legado
        tripSummary: {
          name: trip.name,
          startDate: trip.startDate || null,
          endDate: trip.endDate || null,
        },
        createdAt: serverTimestamp(),
        expiresAt: null,
        revoked: false,
      });
      invites.push({ email, token, url: _buildInviteUrl(token) });
    } catch (err) {
      console.warn(
        `[newTrip] failed to create invite for ${email} —`,
        err?.code || '', err?.message || err,
      );
    }
  }

  if (invites.length) {
    try {
      await updateDoc(doc(db, 'trips', trip.id), {
        pendingInvites: invites.map((i) => ({ email: i.email, token: i.token })),
      });
    } catch (err) {
      console.warn('[newTrip] failed to persist pendingInvites:', err?.code || '', err?.message || err);
    }
  }

  return invites;
}

/** Recria o objeto `aiConfig` que vai pra trip (campos da etapa 3). */
function buildAiConfig(state) {
  if (state.routeMode !== 'ai') return null;
  return {
    flow:          state.aiFlow,
    expensesUndefined: !!state.expensesUndefined,
    budgetMode:    state.expensesUndefined ? null : (state.budgetMode || 'category'),
    budgetAmount:  state.expensesUndefined ? null : (state.budgetAmount || null),
    budgetTier:    state.expensesUndefined ? null : state.budgetTier,
    expenseDetail: state.expenseDetail,
    travelStyle:   state.travelStyle,
    itineraryMode: state.itineraryMode,
    departureCity: (state.departureCity || '').trim(),
    alreadyHave:   (state.alreadyHave || '').trim(),
    notes:         (state.aiNotes || '').trim(),
  };
}

/** Junta places do importer com os places escolhidos no wizard (dedupe por nome). */
function mergePlaces(fromImport, fromWizard) {
  const result = [];
  const seen = new Set();
  const push = (p) => {
    if (!p?.name) return;
    const key = p.name.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    result.push(p);
  };
  (fromImport || []).forEach(push);
  (fromWizard || []).forEach(push);
  return result;
}

// ====================================================================
//  Invites created (success modal)
// ====================================================================

function showInvitesCreatedModal(invites, trip) {
  return new Promise((resolve) => {
    const overlay = document.createElement('div');
    overlay.className = 'settings-modal newtrip-invites-modal';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.innerHTML = `
      <div class="settings-modal-card settings-modal-card--lg glass-sheet">
        <div class="settings-modal-head">
          <p class="eyebrow settings-modal-eyebrow">Convites prontos</p>
          <h3 class="h3 settings-modal-title">${invites.length} link${invites.length === 1 ? '' : 's'} pra compartilhar</h3>
          <p class="settings-modal-help">Mande por WhatsApp ou copie pra colar onde quiser. O link dá acesso à viagem
            <strong>${esc(trip.name)}</strong> assim que a pessoa criar conta no Mapiare.</p>
        </div>
        <div class="settings-modal-body newtrip-invites-list">
          ${invites.map((i) => {
            const wa = _whatsappUrl(i.url, trip.name);
            return `
              <div class="newtrip-invite-row">
                <span class="newtrip-invite-email mono">${esc(i.email)}</span>
                <input type="text" readonly value="${esc(i.url)}" class="input newtrip-invite-url"/>
                <span class="newtrip-invite-actions">
                  <a class="newtrip-invite-wa" href="${esc(wa)}" target="_blank" rel="noopener noreferrer"
                     aria-label="Compartilhar por WhatsApp" title="Compartilhar por WhatsApp">
                    ${WHATSAPP_SVG}
                  </a>
                  <button type="button" class="btn btn-secondary btn-sm newtrip-invite-copy" data-copy="${esc(i.url)}">
                    ${svgIcon('copy', 14)} Copiar
                  </button>
                </span>
              </div>
            `;
          }).join('')}
        </div>
        <div class="settings-modal-foot">
          <button type="button" class="btn btn-primary" data-action="done">Pronto</button>
        </div>
      </div>
    `;
    document.body.appendChild(overlay);

    function close() { overlay.remove(); resolve(); }

    overlay.addEventListener('click', async (e) => {
      const copyBtn = e.target.closest('[data-copy]');
      if (copyBtn) {
        e.preventDefault();
        try {
          await navigator.clipboard.writeText(copyBtn.dataset.copy);
          const orig = copyBtn.innerHTML;
          copyBtn.innerHTML = `${svgIcon('check', 14)} Copiado`;
          setTimeout(() => { copyBtn.innerHTML = orig; }, 1500);
        } catch {}
        return;
      }
      if (e.target.closest('[data-action="done"]') || e.target === overlay) {
        close();
      }
    });

    document.addEventListener('keydown', function k(ev) {
      if (ev.key === 'Escape') { document.removeEventListener('keydown', k); close(); }
    });
  });
}

function _whatsappUrl(inviteUrl, tripName) {
  const text = `Olá! Te convidei pra acompanhar a viagem "${tripName || 'Mapiare'}" no Mapiare. ` +
               `Acesse o link e crie sua conta: ${inviteUrl}`;
  return `https://wa.me/?text=${encodeURIComponent(text)}`;
}

// ====================================================================
//  Google Places
// ====================================================================

/**
 * Bind do Google Places Autocomplete num input.
 *
 * Opções:
 *   - types: array de tipos do Places API (default: ['geocode','establishment']).
 *       Pra cidade de partida usar ['(cities)']; pra lugares amplos manter default.
 *   - clearAfter: se true (default), limpa o input depois do pick — útil pra
 *       inputs do tipo "adicionar chip". Pra inputs single-value (cidade
 *       de partida), passar false pra manter o texto escolhido visível.
 */
// Carrega o SDK do Google Maps lazy (idempotente — `loadGoogleMaps` é
// memoizado em map/places.js) e anexa o autocomplete no input. Antes
// dependia de polling em `window.google` esperando que algum outro módulo
// carregasse o SDK — mas a home não tem mapa, ninguém carregava, polling
// expirava em 10s e os 3 campos com autocomplete (Lugares, Cidade de
// partida, must-haves) ficavam mudos. Agora chamamos `loadGoogleMaps()`
// explicitamente.
// Delegado pro autocomplete compartilhado (dropdown próprio amarrado ao
// placeId exato de cada sugestão). Antes usava o widget legado do Google, que
// tinha o bug de "seleciono uma sugestão e preenche outro endereço". Mantém a
// assinatura {types, clearAfter} dos callers; `types: ['(cities)']` vira o
// modo "cidades" do helper compartilhado.
export function setupPlacesAutocomplete(input, onPick, opts = {}) {
  if (!input) return () => {};
  const cities = Array.isArray(opts.types) && opts.types.includes('(cities)');
  const clearAfter = opts.clearAfter !== false;

  let detach = () => {};
  let cancelled = false;
  attachPlacesAutocomplete(
    input,
    (place) => { if (!cancelled) onPick(place); },
    { clearAfter, cities, includeCountry: true },
  ).then((d) => { if (cancelled) { try { d(); } catch {} } else { detach = d; } });

  return () => { cancelled = true; try { detach(); } catch {} };
}

// ====================================================================
//  HTML
// ====================================================================

function MODAL_HTML(state) {
  return `
    <form class="settings-modal-card settings-modal-card--lg newtrip-card newtrip-card--wizard glass-sheet" novalidate>
      <span class="newtrip-grabhandle" aria-hidden="true"></span>
      <button type="button" class="newtrip-close" data-action="close" aria-label="Fechar">${svgIcon('close', 16)}</button>

      <div class="newtrip-head">
        <p class="eyebrow nt-eyebrow" data-slot="eyebrow">ETAPA 1 DE 3</p>
        <h3 class="h2 nt-title" data-slot="step-title">Nova viagem</h3>
      </div>

      <div class="nt-stepper" data-slot="stepper"></div>

      <div class="newtrip-error" data-slot="error" role="alert"></div>

      <div class="newtrip-body">
        ${STEP_BASIC_HTML(state)}
        ${STEP_ROUTE_HTML(state)}
        ${STEP_AI_HTML(state)}
        ${STEP_AI_EXTRA_HTML(state)}
        ${STEP_IMPORT_HTML(state)}
        ${STEP_INVITES_HTML(state)}
      </div>

      <div class="newtrip-foot newtrip-foot--wizard">
        <button type="button" class="btn btn-ghost newtrip-foot-back" data-slot="foot-left">Cancelar</button>
        <span class="newtrip-foot-counter mono" data-slot="counter">1 / 3</span>
        <button type="button" class="btn btn-primary newtrip-foot-next" data-slot="foot-right">Avançar ${svgIcon('arrowR', 14)}</button>
      </div>
    </form>
  `;
}

function STEP_BASIC_HTML(state) {
  return `
    <div class="nt-step-panel is-active" data-step="basic">
      <div class="newtrip-field">
        <div class="newtrip-field-head">
          ${labelWithIcon('type', 'Título da viagem', 'nt-title')}
          <span class="newtrip-required">(obrigatório)</span>
        </div>
        <input class="input" id="nt-title" placeholder="Ex.: Suíça · Alpes &amp; Trens" maxlength="100" required autocomplete="off"/>
      </div>

      <!-- Período + Viajantes na mesma linha pra economizar altura. -->
      <div class="nt-basic-row">
        <div class="newtrip-field">
          ${labelWithIcon('calendar', 'Período')}
          <div data-slot="date-range"></div>
          <div class="newtrip-field nt-numdays-field" data-slot="numdays-field">
            ${labelWithIcon('calendar', 'Número de dias', 'nt-numdays')}
            <div class="nt-numdays-row">
              <input type="range" id="nt-numdays" min="1" max="33" value="${state.numDays || 7}" class="nt-numdays-slider"/>
              <output class="nt-numdays-output" data-slot="numdays-value">${state.numDays || 7} dias</output>
            </div>
            <span class="newtrip-hint">1 dia · 33 dias</span>
          </div>
          <p class="newtrip-warn" data-slot="days-warn"></p>
        </div>

        <div class="newtrip-field">
          ${labelWithIcon('users', 'Viajantes')}
          <div class="nt-travelers nt-travelers--compact">
            ${TRAVELER_STEPPER('adults', 'Adultos', state.adults, 1, 20)}
            ${TRAVELER_STEPPER('children', 'Crianças', state.children, 0, 10)}
          </div>
        </div>
      </div>

      <!-- Cidade de partida — movida da etapa de IA pro Básico. -->
      <div class="newtrip-field">
        <div class="newtrip-field-head">
          ${labelWithIcon('plane', 'Cidade de partida', 'nt-departure-city')}
          <span class="newtrip-required">(obrigatório)</span>
        </div>
        <div class="newtrip-input-wrap">
          <span class="newtrip-input-icon">${svgIcon('search', 14)}</span>
          <input class="input newtrip-input--padded" id="nt-departure-city" type="text"
            placeholder="Ex.: São Paulo" autocomplete="off" required/>
        </div>
        <span class="newtrip-hint">De onde a viagem começa — usamos pra calcular o transporte de ida e volta.</span>
      </div>

      <div class="newtrip-field">
        <div class="newtrip-field-head">
          ${labelWithIcon('pin', 'Destinos', 'nt-place-input')}
          <span class="newtrip-required">(obrigatório)</span>
        </div>
        <div class="newtrip-input-wrap">
          <span class="newtrip-input-icon">${svgIcon('search', 14)}</span>
          <input class="input newtrip-input--padded" id="nt-place-input" type="text"
            placeholder="Buscar país, cidade ou ponto turístico…" autocomplete="off"/>
        </div>
        <div class="newtrip-chips" data-slot="place-chips"></div>
        <span class="newtrip-hint">Adicione pelo menos 1 destino — pode ser país, cidade ou atração.</span>
      </div>
    </div>
  `;
}

function STEP_ROUTE_HTML(/* state */) {
  return `
    <div class="nt-step-panel" data-step="route">
      <p class="nt-step-help">Escolha como começar — você pode editar tudo depois.</p>

      <div class="nt-route-cards">
        ${ROUTE_CARD({
          mode:     'ai-generate',
          icon:     'sparkles',
          title:    'Gerar roteiro com IA',
          desc:     'Geramos um roteiro completo do zero — dias, hotéis e gastos.',
          descSlot: 'ai-generate-desc',
          badge:    { text: 'Verificando…', tone: 'soft', slot: 'ai-generate-badge' },
        })}
        ${ROUTE_CARD({
          mode:    'ai-prompt',
          icon:    'copy',
          title:   'Usar sua IA (copiar prompt)',
          desc:    'Roteiro mais simples: copie o prompt, gere na sua IA favorita e importe.',
          badge:   { text: 'Grátis', tone: 'success' },
        })}
        ${ROUTE_CARD({
          mode:   'manual',
          icon:   'edit',
          title:  'Roteiro manual',
          desc:   'Cria a viagem vazia. Você adiciona dias, paradas e gastos no seu ritmo.',
        })}
        ${ROUTE_CARD({
          mode:    'preset',
          icon:    'map',
          title:   'Escolher roteiro existente',
          desc:    'Roteiros testados pela comunidade.',
          badge:   { text: 'Em breve', tone: 'soft' },
        })}
      </div>

      <!-- Sub-painel: Pré-prontos (lista carregada via loadPresets) -->
      <div class="nt-route-panel" data-slot="preset-panel">
        <p class="eyebrow nt-eyebrow" data-slot="preset-eyebrow">Sugestões para você</p>
        <div class="nt-preset-list" data-slot="preset-list"></div>
      </div>
    </div>
  `;
}

function TRAVELER_STEPPER(field, label, value, min, max) {
  return `
    <div class="nt-traveler" data-traveler="${field}">
      <span class="nt-traveler-label">${esc(label)}</span>
      <div class="nt-traveler-stepper">
        <button type="button" class="nt-traveler-btn" data-traveler-dec aria-label="Diminuir ${esc(label.toLowerCase())}">−</button>
        <span class="nt-traveler-value" data-traveler-value>${value}</span>
        <button type="button" class="nt-traveler-btn" data-traveler-inc aria-label="Aumentar ${esc(label.toLowerCase())}">+</button>
      </div>
      <input type="hidden" data-traveler-input data-min="${min}" data-max="${max}" value="${value}"/>
    </div>
  `;
}

function ROUTE_CARD({ mode, icon, title, desc, descSlot, badge, disabled }) {
  const cls = ['nt-route-card'];
  if (disabled) cls.push('is-disabled');
  // badge.slot / descSlot → data-slot pra renderRouteAiQuota() atualizar
  // dinamicamente (badge de cota e link premium do card "Gerar com IA").
  const badgeHtml = badge
    ? `<span class="nt-badge nt-badge--${badge.tone || 'soft'}"`
      + `${badge.slot ? ` data-slot="${badge.slot}"` : ''}>${esc(badge.text)}</span>`
    : '';
  return `
    <button type="button" class="${cls.join(' ')}" data-route-card="${mode}" ${disabled ? 'aria-disabled="true"' : ''}>
      <span class="nt-route-icon">${svgIcon(icon, 22)}</span>
      <span class="nt-route-body">
        <span class="nt-route-title">
          ${esc(title)}
          ${badgeHtml}
        </span>
        <span class="nt-route-desc"${descSlot ? ` data-slot="${descSlot}"` : ''}>${esc(desc)}</span>
      </span>
      <span class="nt-route-radio" aria-hidden="true">${svgIcon('check', 12)}</span>
    </button>
  `;
}

// Etapa "Gastos" — switch "não definir gastos agora" + (quando definir) teto
// de gastos (valor OU categoria) e detalhamento. As cotações de moedas
// passaram a ser definidas pela IA (com base no destino) e persistidas
// automaticamente em trips/{id}/settings — sem botão "Moedas da viagem".
function STEP_AI_HTML(state) {
  return `
    <div class="nt-step-panel" data-step="ai">

      <!-- Switch: não definir gastos agora -->
      <label class="nt-switch">
        <input type="checkbox" id="nt-expenses-undefined" ${state.expensesUndefined ? 'checked' : ''}/>
        <span class="nt-switch-track" aria-hidden="true"></span>
        <span class="nt-switch-label">Não definir gastos agora</span>
      </label>
      <p class="newtrip-hint">Você pode planejar os gastos depois, direto na viagem.</p>

      <!-- Conteúdo de gastos — escondido quando o switch está ligado -->
      <div data-slot="expenses-config" ${state.expensesUndefined ? 'hidden' : ''}>

        <!-- Teto de gastos: toggle Valor vs Categoria -->
        <div class="newtrip-field">
          ${labelWithIcon('wallet', 'Teto de gastos')}
          <div class="nt-segmented nt-budget-mode">
            <button type="button" class="nt-segmented-btn ${state.budgetMode === 'category' ? 'is-active' : ''}" data-budget-mode="category">
              Categoria
            </button>
            <button type="button" class="nt-segmented-btn ${state.budgetMode === 'amount' ? 'is-active' : ''}" data-budget-mode="amount">
              Valor do orçamento
            </button>
          </div>

          <!-- Painel: Categorias (4 botões + legenda da categoria ativa) -->
          <div data-slot="budget-category-pane" ${state.budgetMode === 'category' ? '' : 'hidden'}>
            <div class="nt-budget-grid">
              ${BUDGET_TIERS.map((t) => `
                <button type="button" class="nt-budget-btn ${t.id === state.budgetTier ? 'is-active' : ''}" data-budget-tier="${t.id}">
                  <span class="nt-budget-symbol">${esc(t.symbol)}</span>
                  <span class="nt-budget-label">${esc(t.label)}</span>
                </button>
              `).join('')}
            </div>
            <p class="nt-budget-legend" data-slot="budget-legend">${esc(BUDGET_TIER_LEGENDS[state.budgetTier] || '')}</p>
          </div>

          <!-- Painel: Valor do orçamento (R$) — input com mascara 0,000.00.
               O tipo text (e nao number) deixa a gente exibir a mascara; o
               valor numerico real fica em STATE.budgetAmount. -->
          <div data-slot="budget-amount-pane" ${state.budgetMode === 'amount' ? '' : 'hidden'}>
            <div class="nt-budget-amount-row">
              <span class="nt-budget-amount-currency mono">R$</span>
              <input class="input nt-budget-amount-input" id="nt-budget-amount" type="text"
                inputmode="decimal" autocomplete="off"
                placeholder="0,000.00"
                value="${state.budgetAmount ? formatBudgetAmount(state.budgetAmount) : ''}"/>
            </div>
            <p class="newtrip-hint nt-budget-amount-hint">Total da viagem em reais. A IA vai distribuir entre transporte, hospedagem, alimentação e atrações respeitando esse teto.</p>
          </div>
        </div>

        <!-- Detalhamento dos gastos -->
        <div class="newtrip-field">
          ${labelWithIcon('tag', 'Detalhamento dos gastos')}
          <div class="nt-segmented">
            ${Object.entries(EXPENSE_DETAIL).map(([id, label]) => `
              <button type="button" class="nt-segmented-btn ${id === state.expenseDetail ? 'is-active' : ''}" data-expense-detail="${id}">
                ${esc(label)}
              </button>
            `).join('')}
          </div>
          <p class="nt-budget-legend" data-slot="expense-detail-legend">${esc(EXPENSE_DETAIL_LEGENDS[state.expenseDetail] || '')}</p>
        </div>

      </div>

    </div>
  `;
}

// Etapa "Detalhes" — sequência: estilo da viagem → estilo do itinerário →
// o que já tem da viagem → outras observações. As atrações obrigatórias
// saíram (a IA agora coleta isso pelo campo de observações).
function STEP_AI_EXTRA_HTML(state) {
  return `
    <div class="nt-step-panel" data-step="aiExtra">

      <!-- Estilo (ritmo + itinerário) — só no fluxo "Gerar com IA".
           No "Copiar prompt" estes campos ficam ocultos (valores automáticos:
           equilibrada / básico). Ver renderStepVisibility. -->
      <div data-slot="ai-style-fields">

        <!-- Estilo da viagem (ritmo) -->
        <div class="newtrip-field">
          ${labelWithIcon('compass', 'Estilo da viagem')}
          <div class="nt-radio-list">
            ${TRAVEL_STYLES.map((s) => `
              <button type="button" class="nt-radio-card ${s.id === state.travelStyle ? 'is-active' : ''}" data-travel-style="${s.id}">
                <span class="nt-radio-dot" aria-hidden="true"></span>
                <span class="nt-radio-body">
                  <span class="nt-radio-title">${esc(s.label)}</span>
                  <span class="nt-radio-desc">${esc(s.desc)}</span>
                </span>
              </button>
            `).join('')}
          </div>
        </div>

        <!-- Estilo do itinerário -->
        <div class="newtrip-field newtrip-field--spaced">
          ${labelWithIcon('route', 'Estilo do itinerário')}
          <div class="nt-radio-list">
            ${ITINERARY_MODES.map((m) => `
              <button type="button" class="nt-radio-card ${m.id === state.itineraryMode ? 'is-active' : ''}" data-itinerary-mode="${m.id}">
                <span class="nt-radio-dot" aria-hidden="true"></span>
                <span class="nt-radio-body">
                  <span class="nt-radio-title">${esc(m.title)}</span>
                  <span class="nt-radio-desc">${esc(m.desc)}</span>
                </span>
              </button>
            `).join('')}
          </div>
        </div>

      </div><!-- /ai-style-fields -->

      <!-- Conte pra gente o que já tem da viagem -->
      <div class="newtrip-field">
        <div class="newtrip-field-head">
          ${labelWithIcon('bookmark', 'Conte pra gente o que já tem da viagem', 'nt-already-have')}
          <span class="newtrip-optional">opcional</span>
        </div>
        <textarea class="input nt-textarea" id="nt-already-have" rows="3"
          placeholder="Ex.: já comprei passagem de SP→Lisboa, hotel reservado no Chiado, ingresso da Sagrada Família…">${esc(state.alreadyHave || '')}</textarea>
        <span class="newtrip-hint">Liste o que já está comprado ou reservado — hotel, passagem, ingresso, transfer. A IA respeita isso no roteiro e nos gastos.</span>
      </div>

      <!-- Outras observações -->
      <div class="newtrip-field">
        <div class="newtrip-field-head">
          ${labelWithIcon('message', 'Outras observações', 'nt-ai-notes')}
          <span class="newtrip-optional">opcional</span>
        </div>
        <textarea class="input nt-textarea" id="nt-ai-notes" rows="3"
          placeholder="Adicione observações, como lugares que quer visitar, desejos, aqui o texto é seu, converse com a IA">${esc(state.aiNotes || '')}</textarea>
        <span class="newtrip-hint">Ex.: viajamos com criança pequena, evitar dirigir, foco em gastronomia local, quero conhecer Hallstatt…</span>
      </div>

    </div>
  `;
}

function STEP_IMPORT_HTML(/* state */) {
  return `
    <div class="nt-step-panel" data-step="import">
      <p class="nt-step-help">Cole o prompt na sua IA, gere o JSON e traga o resultado — por <strong>arquivo</strong> ou <strong>colando o texto</strong>.</p>

      <!-- Prévia do prompt — copie aqui, cole na sua IA e gere o JSON -->
      <div class="nt-prompt-panel is-active" data-slot="prompt-panel">
        <div class="nt-prompt-head">
          <p class="eyebrow nt-eyebrow">Prévia do prompt</p>
          <button type="button" class="btn btn-secondary btn-sm nt-prompt-copy" data-action="copy-prompt">
            ${svgIcon('copy', 14)} <span>Copiar</span>
          </button>
        </div>
        <pre class="nt-prompt-preview mono" data-slot="prompt-preview"></pre>
        <p class="newtrip-hint">
          Copie o prompt, cole na sua IA favorita (ChatGPT, Claude, Gemini…) e traga o JSON gerado abaixo.
        </p>
      </div>

      <!-- Duas vias de import: enviar arquivo OU colar o JSON -->
      <div class="nt-segmented" data-slot="import-mode">
        <button type="button" class="nt-segmented-btn is-active" data-import-mode="file">Enviar arquivo</button>
        <button type="button" class="nt-segmented-btn" data-import-mode="paste">Colar JSON</button>
      </div>

      <!-- Via 1: upload de arquivo -->
      <div data-slot="import-file-pane">
        <label class="nt-dropzone" data-slot="dropzone">
          <input type="file" id="nt-import-input" accept=".json,application/json" hidden/>

          <div class="nt-dropzone-empty is-shown" data-slot="dz-empty">
            <span class="nt-dropzone-icon">${svgIcon('copy', 22)}</span>
            <strong class="nt-dropzone-title">Adicionar JSON da sua IA</strong>
            <span class="nt-dropzone-hint">.json · até 1 MB</span>
          </div>

          <div class="nt-dropzone-file" data-slot="dz-file">
            <span class="nt-dropzone-icon nt-dropzone-icon--ok">${svgIcon('check', 22)}</span>
            <span class="nt-dropzone-meta">
              <strong class="nt-dropzone-filename" data-slot="dz-filename">arquivo.json</strong>
              <span class="nt-dropzone-size" data-slot="dz-size">— KB · pronto para importar</span>
            </span>
            <button type="button" class="nt-dropzone-trash" data-action="dz-clear" aria-label="Remover arquivo" title="Remover arquivo">${svgIcon('trash', 16)}</button>
          </div>
        </label>
      </div>

      <!-- Via 2: colar o JSON direto -->
      <div data-slot="import-paste-pane" hidden>
        <textarea class="input nt-textarea mono" id="nt-import-paste" rows="6"
          placeholder="Cole aqui o JSON que a IA gerou…"></textarea>
        <button type="button" class="btn btn-secondary btn-sm" data-action="paste-process">
          ${svgIcon('check', 14)} <span>Usar este JSON</span>
        </button>
      </div>

      <div class="nt-import-summary" data-slot="import-summary"></div>
      <div class="nt-import-error" data-slot="import-error" role="alert"></div>

      <div class="nt-callout">
        <span class="nt-callout-icon">${svgIcon('sparkles', 16)}</span>
        <span>Dica: o prompt já pede o formato correto — basta trazer o bloco JSON que a IA gera, por arquivo ou colando.</span>
      </div>
    </div>
  `;
}

function STEP_INVITES_HTML(/* state */) {
  return `
    <div class="nt-step-panel" data-step="invites">
      <p class="nt-step-help">Quem mais vai junto? (opcional)</p>

      <div class="newtrip-field">
        <div class="newtrip-field-head">
          ${labelWithIcon('user', 'Convidados')}
          <span class="newtrip-optional">opcional</span>
        </div>
        <div data-slot="guest-invite-mount"></div>
        <div class="newtrip-chips" data-slot="guest-chips"></div>
        <span class="newtrip-hint">Busque alguém pelo nome ou email — ou digite um email novo e aperte Enter. Cada um recebe um link de convite.</span>
      </div>
    </div>
  `;
}

// ====================================================================
//  helpers
// ====================================================================

function showError(el, msg) {
  if (!el) return;
  el.textContent = msg;
  el.classList.add('visible');
}
function hideError(el) {
  if (!el) return;
  el.textContent = '';
  el.classList.remove('visible');
}

function flashInputError(input, msg) {
  if (!input) return;
  const orig = input.placeholder;
  input.placeholder = msg;
  input.classList.add('newtrip-input--error');
  setTimeout(() => {
    input.placeholder = orig;
    input.classList.remove('newtrip-input--error');
  }, 1200);
}

function _isValidEmail(s) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
}

/**
 * Traduz erros do Firebase pra mensagens amigáveis em PT-BR.
 * Pra "permission-denied" inclui dica de fazer deploy das rules.
 */
function friendlyErrorMessage(err) {
  const code = err?.code || '';
  const msg = err?.message || 'Erro ao criar viagem';
  if (code === 'permission-denied' || /permission|insufficient/i.test(msg)) {
    return 'Sem permissão pra salvar essa viagem. Confirma que as Firestore Rules foram publicadas (`firebase deploy --only firestore:rules`).';
  }
  if (code === 'unavailable' || code === 'failed-precondition') {
    return 'Sem conexão com o Firestore agora. Verifica sua internet e tente de novo.';
  }
  if (code === 'unauthenticated') {
    return 'Sua sessão expirou. Faça login novamente.';
  }
  return msg;
}

function _randomToken() {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

function _buildInviteUrl(token) {
  // Base pública do site (não location.origin — no app é localhost).
  return inviteUrl(token);
}

/**
 * Formata um número como máscara "0,000.00" (vírgula = milhar, ponto = decimal).
 * Usado pra exibir STATE.budgetAmount no input de "Valor do orçamento" do passo
 * Gastos. Mantém sempre 2 casas decimais.
 */
export function formatBudgetAmount(n) {
  const num = Number(n);
  if (!Number.isFinite(num) || num <= 0) return '';
  return num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Anexa máscara LIVE de moeda no input: estilo "calculadora". Cada dígito
 * digitado vira a próxima casa decimal e empurra o restante uma posição
 * pra esquerda; backspace remove o último dígito.
 *
 * Aceita SÓ dígitos no buffer (ignora qualquer outra tecla). Limita a 12
 * dígitos = até R$ 9.999.999.999,99 (mais que isso é input de erro).
 *
 * O callback recebe o número JS já parseado (ou null se vazio) — mantém
 * STATE.budgetAmount sincronizado a cada digitação. Não dispara
 * onChange/onInput sintéticos: tudo via DOM nativo.
 *
 * Não tenta preservar a posição do cursor: como o número cresce pela
 * direita (vírgula fixa nas 2 casas decimais), o cursor sempre no fim é
 * o comportamento natural pro usuário.
 */
export function applyBudgetMask(input, onChange) {
  const MAX_DIGITS = 12; // R$ 9.999.999.999,99
  // Estado interno = string de dígitos (ex: "" → 0,00; "1" → 0,01; "123" → 1,23)
  let digits = '';

  // Hidrata com o valor atual do input (se vier preenchido).
  const initial = parseBudgetAmount(input.value);
  if (Number.isFinite(initial) && initial > 0) {
    digits = String(Math.round(initial * 100));
    input.value = formatFromDigits(digits);
  }

  function formatFromDigits(d) {
    if (!d) return '';
    const padded = d.padStart(3, '0');
    const cents = padded.slice(-2);
    const reais = padded.slice(0, -2).replace(/^0+(?=\d)/, ''); // strip leading zeros
    // Insere vírgula a cada 3 dígitos do inteiro (formato "1,234").
    const withCommas = reais.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return `${withCommas}.${cents}`;
  }

  function commit() {
    input.value = formatFromDigits(digits);
    // Move o cursor pro fim — comportamento natural de máscara que cresce
    // à direita. Browsers só permitem setSelectionRange em inputs text.
    try {
      const end = input.value.length;
      input.setSelectionRange(end, end);
    } catch { /* alguns inputs (number) rejeitam — ignora */ }
    const num = digits ? Number(digits) / 100 : null;
    onChange(num);
  }

  input.addEventListener('input', (e) => {
    // Pega TODOS os dígitos que sobraram após a edição — funciona pra
    // typing, paste, autofill, undo. Recalcula do zero sempre, é mais
    // robusto que tentar interpretar inputType + inserted.
    const raw = String(input.value || '').replace(/\D+/g, '');
    digits = raw.slice(-MAX_DIGITS); // limita
    // Edge case: usuário apagou tudo → zera (mostra placeholder).
    commit();
    // Anti-loop: o setSelectionRange dentro de commit não dispara input,
    // mas comentando aqui pra deixar claro que NÃO existe ciclo.
    void e;
  });

  // Backspace numa string já vazia limpa qualquer ruído residual
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Backspace' && !digits) {
      // Já está zerado — bloqueia o default pra não tentar editar texto
      // "fantasma" deixado por algum autofill (raro mas chato quando ocorre).
      e.preventDefault();
    }
  });
}

/**
 * Converte a string digitada na máscara "0,000.00" pra Number. Tolerante:
 *  - "1,234.50"  → 1234.5
 *  - "1234,50"   → 1234.5  (BR também aceito — vírgula como decimal se NÃO
 *    houver ponto depois dela; ajuda usuários que digitam BR sem perceber)
 *  - "  R$ 1.234,50 " → 1234.5
 *  - vazio / inválido → null
 */
export function parseBudgetAmount(raw) {
  if (raw == null) return null;
  let s = String(raw).trim();
  if (!s) return null;
  s = s.replace(/[R$\s]/gi, '');
  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  if (hasDot && hasComma) {
    // Assume formato US/canônico: vírgula = milhar, ponto = decimal.
    s = s.replace(/,/g, '');
  } else if (hasComma && !hasDot) {
    // Provável formato BR ("1234,50") — só vírgula. Troca pra decimal.
    s = s.replace(',', '.');
  }
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function svgIcon(name, size = 18) {
  const paths = ICONS[name];
  if (!paths) return '';
  return `<svg width="${size}" height="${size}" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${paths}</svg>`;
}

/**
 * Renderiza um <label> com ícone à esquerda do texto, ambos em negrito.
 * Padronizado pros campos do wizard — o ícone dá um cue visual da etapa
 * (ex: calendário pro Período, mala pra Cidade de partida) e o negrito
 * deixa o título mais hierárquico que os hints abaixo.
 *
 * `forId` é opcional — quando presente vira `for=` (associa o label ao
 * input pra acessibilidade). Quando ausente, usa `<span>` (pra blocos
 * sem input direto, como "Estilo da viagem" que tem cards radio).
 */
function labelWithIcon(icon, text, forId) {
  const tag = forId ? 'label' : 'span';
  const attrs = forId ? ` for="${esc(forId)}"` : '';
  return `<${tag} class="label label--icon"${attrs}>`
    + `<span class="label-icon" aria-hidden="true">${svgIcon(icon, 16)}</span>`
    + `<span class="label-text">${esc(text)}</span>`
    + `</${tag}>`;
}

function esc(s) {
  return String(s ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}
